import PhoneDecisionBar from '../components/ui/PhoneDecisionBar';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useVatMode } from './useVatMode';
import { apiGet, apiFetch, apiThrow, API_BASE } from '../utils/api';
import {
  useOrderReferenceArmed,
  useOrderAccountingLists,
  useOrderReferenceData,
} from './useOrderPickerLists';
import { useDepartment } from '../contexts/DepartmentContext';
import { useOrderAuthority } from './useOrderAuthority';
import useSubmitGuard from './useSubmitGuard';
import useListRefreshWhenClean from './useListRefreshWhenClean';
import useIdempotencyKey from './useIdempotencyKey';
import useDirtyContinuation from './useDirtyContinuation';
import useExpandedOrderCore from './useExpandedOrderCore';
import {
  DELIVERY_STAGE_STATUSES,
  RECEIVE_ENABLED_STATUSES,
  RECEIVING_LOCKED_TOOLTIP,
  managerMayCancelOrder,
  isOrderComplete,
} from '../constants/statuses';
import { buildCompleteConfirmMessage, buildReopenConfirmMessage } from '../constants/orderCompletionPrompt';
import { orderLifecycleMenuItem } from '../constants/orderLifecycleMenuItems';
import { BTN } from '../constants/buttons';
import OrderActionsMenu from '../components/OrderActionsMenu';
import { withHireMenu } from '../components/hires/hireOrderMenu';
import OrderPdfMenuItems from '../components/OrderPdfMenuItems';
import { exportSingleOrderCsv } from '../utils/exportSingleOrder';
import { CSV_LABEL, CSV_MENU_LABEL } from '../utils/gagCsvExport';
import OrderExpandedRow from '../components/OrderExpandedRow';
import InlineStatusJump from '../components/InlineStatusJump';
import { makeReceivedItemHandlers } from '../utils/receivedItems';
import { useToastOrNoop } from '../contexts/ToastContext';
import { getNextStepInfo, canPokeOrder, pokeLabel, pokeTitle, isApprovingSomeoneElsesStep } from '../utils/orderStepInfo';
import { isMobileOrderViewport } from './useIsMobileOrderViewport';
import ApproveOnBehalfDialog from '../components/ApproveOnBehalfDialog';
// THE reason dialog in front of skipping a pending approval — shared by the
// `Skip remaining approvals…` menu item AND by `InlineStatusJump`'s forward
// jump, so the two doors onto one act cannot be worded differently.
// 🪤 It is rendered at PAGE level, never inside the Actions menu: `ConfirmDialog`
// is a bare `fixed inset-0` overlay with no portal and `ui/Dropdown` closes on an
// outside `mousedown`, so a dialog mounted in the panel would unmount itself on
// the user's first click in it.
import SkipApprovalsDialog from '../components/SkipApprovalsDialog';
import { refreshOneOrder, refreshOrderRow, mergeStatusJumpResult, withOrderMutationNotice } from '../utils/refreshOneOrder';
import ConfirmDialog from '../components/ConfirmDialog';
import { getOrderNumber } from '../utils/orderTitle';
import { countDelivery, allGoodsReceived } from '../utils/deliveryCountableItems';
import { orderHasHireStillOut } from '../utils/hireBuckets';
import InternalNotesReadOnly from '../components/InternalNotesReadOnly';
import InfoTip from '../components/InfoTip';
import { INTERNAL_NOTES_LABEL, INTERNAL_NOTES_TOOLTIP, INTERNAL_NOTES_PLACEHOLDER } from '../constants/orderNotes';
import {
  SAVE_THEN_ACT_TITLE,
  SAVE_THEN_ACT_KEEP_EDITING,
  saveThenActCopy,
} from '../constants/saveThenActPrompt';
import { FIELD_LABEL_CLASS } from '../constants/inputs';

/**
 * `useDeptOrderExpansion` — THE Department Orders expanded row: its state, its
 * handlers, its page-level dialogs and `renderExpanded(order)`, the node the
 * TABLE row renders (the card row went with the order cards, 2026-10-18).
 *
 * 🔀 EXTRACTED 2026-09-27, VERBATIM, out of `pages/DepartmentOrders.js`, so the
 * Department Hires table can expand a hire into the SAME order panel — owner:
 * *"it should be fairly simple to use the exact same expansion as all of the
 * edit/access rights should work as Hires are just orders"*. Every gate,
 * handler, save path and dialog below is the page's own code moved, not
 * rewritten; the page is now a caller, and every `DepartmentOrders.*` test is
 * the parity proof. The archaeology travelled with each block.
 *
 * 🔑 **WHAT STAYS ON THE CALLING PAGE — the LIST concerns.** The page owns the
 * order list (`orders` / `setOrders` / `fetchOrders`), the poll and its
 * `paused`, the leave guard (ONE `useUnsavedChangesGuard` per page), the
 * department-switch dirty registration, `withDirtyRowPinned`, the deep link and
 * the queue-tab sticky row. Each reads `editDirtyId` from here — one source of
 * truth. `onListRefresh` is what the received-tick cluster calls after a tick
 * (Dept Orders passes its sticky-row `handleItemsReceived`).
 *
 * 🚨 **IT RESOLVES NOTHING NEW.** Every permission is still the server's
 * `detail.can_edit` / `can_action` and `useOrderAuthority().canManageOrder`,
 * exactly where the page read them. Moving JSX grants nobody anything.
 *
 * ⚠️ `setOrders` must be a `useState` setter (stable identity) and `fetchOrders`
 * a stable callback — they appear in dependency arrays here.
 *
 * 🧾 **THE EDITS THE MOVE MADE TO THE MOVED CODE — the whole list** (commit
 * 2dae0839; 8908ba12 for `deferReferenceData`). This sentence said "the
 * dependency arrays are the only edit" until an adversarial review counted:
 * - `setOrders` / `setSuccessMessage` added to five dependency arrays (now
 *   parameters; both `useState` setters, so identity is unchanged);
 * - the row's inline `getNextStepInfo(order, { … })` became `stepInfoFor(order)`,
 *   a `useCallback` here that the page calls per row (same arguments);
 * - the hook calls its OWN `useNavigate()`, `useToastOrNoop()` and
 *   `useDepartment()` (destructuring only the names it reads) — a second
 *   instance of each beside the page's;
 * - the seven-line note above the `SkipApprovalsDialog` import was DROPPED in
 *   the move and restored (Phase 4) — it is the reason `dialogs` renders at
 *   page level;
 * - `deferReferenceData` (opt-in, default `false` = unchanged): a guard and a
 *   dependency on the three reference-data effects, for the hires Table.
 */
export default function useDeptOrderExpansion({
  setOrders: setOrdersFromCaller,
  fetchOrders,
  isTableLayout,
  setSuccessMessage,
  onListRefresh: handleItemsReceived,
  // 🔀 2026-10-29 (review T-1) — `(orderId, busy)` at a tick's start and end, so the page's
  // `hooks/useHeldOpenRows.js` holds the row BEFORE the PATCH. Default `null`: no caller changes.
  onTickBusyChange = null,
  deferReferenceData = false,
  // 🔀 2026-09-28 (owner: *"the main header action menu to have off hire all,
  // then each line item has the action menu … to offhire each line item"*):
  // `{ arrangeMenu(order, { dirty }, menuProps), lineItems(order, item, { dirty }) }`,
  // FINISHED, already-gated menu items built by the hires pages'
  // `components/hires/HireOrderExpansion.js`. Default `null` — Department
  // Orders passes nothing and renders byte-identically (its parity goldens).
  // Offered on the HIRES pages' expansion only, by ruling.
  hireActions = null,
  // 🔀 2026-09-29 (owner: the Hires tab counts and the paperclip did not move
  // until a reload): called after ANY mutation of an order in this panel, so
  // a hires page can silently refetch its OWN list (`GET /api/hires`), which
  // no per-order merge reaches. Default `null` — Department Orders passes
  // nothing and `setOrders` below is its own setter, unchanged. See
  // `utils/refreshOneOrder.js#withOrderMutationNotice`.
  onOrderMutated = null,
}) {
  // Every row write below goes through this; `refreshOrderDetail` (a hires
  // page's OWN follow-up refresh) keeps the raw setter.
  const setOrders = useMemo(
    () => withOrderMutationNotice(setOrdersFromCaller, onOrderMutated),
    [setOrdersFromCaller, onOrderMutated]
  );
  const navigate = useNavigate();
  const { formatMoney } = useVatMode();

  // Expandable row state + every write to the detail cache, shared with
  // `useMyOrderExpansion` and `pages/Approvals.js` since 2026-10-03.
  // 🔑 `useExpandedOrderCore` runs NO effect, so calling it here cannot move
  // this page's mount request order (the parity goldens record that order) —
  // see its docblock for why that is the load-bearing property.
  const {
    expandedOrders, setExpandedOrders,
    orderDetails, setOrderDetails,
    detailsLoading, setDetailsLoading,
    pokeLoading,
    updateOrderListFromItem,
    handleCostCodeChange,
    handlePaymentTypeChange,
    handleItemNotifyUpdated,
    handlePoNumberSaved,
    handleExpectedDeliverySaved,
    handleOrderFilesChanged,
    handleOrderDetailInvalidated,
    handlePoke,
    requestOrderReason,
    requestRejectionReason,
    reasonDialog,
  } = useExpandedOrderCore({ setOrders });
  const [costCodes, setCostCodes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  // Reference data for the expand-to-edit form. Fetched ONCE PER PAGE (see the
  // effect below), never per expanded row.
  const [productions, setProductions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Supplier names typed on PAST APPROVED ORDERS — the SECOND tier of the
  // supplier picker in the inline editor, never merged into `suppliers`
  // (see utils/supplierOptions.js).
  const [supplierSuggestions, setSupplierSuggestions] = useState([]);
  const [subDepartments, setSubDepartments] = useState([]);
  const [gags, setGags] = useState([]);
  // 🚨 WHICH REFERENCE LISTS ARE EMPTY BECAUSE THE REQUEST FAILED — 2026-08-22.
  //
  // `[]` is the shape of "this department has none" AND the shape of "nobody
  // asked", and `components/OrderForm.js` makes confident claims from it in
  // the inline editor: *"No suppliers set up for this department yet"* (worded
  // to send the reader off to CREATE supplier records), and — via an emptied
  // option list — every coded line silently redrawn as `-- None --`. The
  // failure happens here and the claim is rendered there, so the flag has to
  // travel. See the `loadFailures` prop on `OrderForm`.
  const [loadFailures, setLoadFailures] = useState({});
  // Expand-to-edit dirty guard. `editDirtyId` holds the id of the row whose
  // inline form has unsaved changes — an ID, not a boolean, so a closing
  // panel's unmount cleanup (which reports `false`) can only clear ITS OWN
  // flag and never a freshly-expanded row's. Same reasoning as
  // GagGroupTable's `dirtyKey`.
  const [editDirtyId, setEditDirtyId] = useState(null);
  // 🔀 2026-09-28 — every gesture that would lose the dirty edit ASKS (the
  // editor's three-way prompt, bumped through `promptSignal`) and then does
  // what was clicked. Replaced this hook's `blockIfDirty` + amber pulse; see
  // `hooks/useDirtyContinuation.js`.
  const {
    continueWhenClean,
    promptSignal: collapseRequestSignal,
    onPromptResolved,
  } = useDirtyContinuation({ dirty: editDirtyId !== null });
  // 🔀 2026-09-29 (owner: "fix all") — a panel change the COLLAPSED ROW shows
  // (an attachment's paperclip, a Status Override's `can_action`) refetches the
  // page's list silently; deferred while the edit is dirty, the poll's own rule.
  // 🔻 Its `{ keepSticky: true }` went 2026-10-28 with the option: no list
  // reload releases the held row any more (`hooks/useHeldOpenRows.js`).
  const requestListRefresh = useListRefreshWhenClean(
    () => fetchOrders(true),
    editDirtyId !== null
  );
  // ── THE UNSAVED-CHANGES PROMPT IN FRONT OF SAVE-THEN-ACT (2026-08-10) ────
  // `{ orderId, kind, run }` while the dialog is open, else null. `run` is the
  // SAME closure the click built — held, not rebuilt — so confirming does
  // exactly what pressing the button did yesterday.
  //
  // 🔑 An OBJECT and not three pieces of state: they are one decision and can
  // only ever be set or cleared together, and `useState(null)` on a wrapper
  // also sidesteps the functional-update trap that storing a bare function in
  // state walks straight into (`setX(fn)` would CALL it).
  //
  // ⚠️ ONE at a time, page-wide, and that needs no enforcing: at most one row
  // is expanded here (accordion) and at most one is dirty.
  const [pendingSaveAction, setPendingSaveAction] = useState(null);
  // Order Completion (2026-08-16) — `{ order, mode: 'complete' | 'reopen' }`
  // while the ALWAYS-SHOWN confirm dialog is open, else null. The Mark
  // Complete / Re-open menu items set this instead of calling `runRowAction`
  // directly; `runRowAction` fires only from the dialog's Continue, so
  // Cancel writes nothing — the same "hold the intent, act on confirm" shape
  // as `pendingSaveAction` above.
  const [completionPrompt, setCompletionPrompt] = useState(null);
  // Approve-on-behalf (2026-08-18 owner ruling, the Jake Dimmer incident —
  // see `components/ApproveOnBehalfDialog.js`'s docblock). `{ order, direct }`
  // while the dialog is open, else null — the same "hold the intent, act on
  // confirm" shape as `completionPrompt` above. `direct` distinguishes the
  // Override quick-action (which bypasses save-then-act and calls
  // `handleAction` straight, exactly as it did before this change) from the
  // ordinary Approve button/menu item (which goes through `runRowAction`).
  const [approveOnBehalfPrompt, setApproveOnBehalfPrompt] = useState(null);
  // 🔀 2026-09-14 — the MANDATORY reason typed into that dialog (owner:
  // approve on behalf *"is still an override"*). Separate state for the same
  // controlled-textarea reason as `overrideReason` below; cleared both ways out.
  const [approveOnBehalfReason, setApproveOnBehalfReason] = useState('');
  // Skip remaining approvals (2026-09-13 owner ruling) — the order whose
  // override dialog is open, else null. Same "hold the intent, act on confirm"
  // shape as the three prompts above; Cancel writes nothing.
  // 🔑 THE REASON IS SEPARATE STATE, NOT A FIELD ON THE PROMPT OBJECT, because
  // `ConfirmDialog`'s `promptValue`/`onPromptChange` is a CONTROLLED textarea:
  // every keystroke would otherwise replace the prompt object and remount the
  // dialog. It is cleared wherever the prompt is cleared, both ways out.
  const [overridePrompt, setOverridePrompt] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  // Admin notes editing state
  const [editingNotes, setEditingNotes] = useState({}); // { orderId: 'text' }
  const [savingNotes, setSavingNotes] = useState({});   // { orderId: true }

  const currentUser = localStorage.getItem('sfx_requested_by') || '';
  // The viewer's own user_id, for identity comparisons (never compare display names).
  // Same key AuthContext syncs on every session refresh; MyOrders.js reads it the same way.
  const currentUserId = localStorage.getItem('sfx_user_id') || '';
  // 🔑 `capabilitiesFor` (added 2026-09-13) resolves the viewer's capability set
  // IN A NAMED DEPARTMENT, from the assignment rows the server already scored —
  // it is a lookup, not a second derivation of authority. The Skip-approvals
  // item needs it because the server gate for that one route is
  // `canManageDepartmentSettings` in THE ORDER'S OWN department, and
  // `capabilities` (the ACTIVE department's set) would be a different question.
  // See `hooks/useOrderAuthority.js` for the same argument at length.
  const { isAdmin, isBuyer, canManageStores, activeRole, activeDepartment, assignments, capabilitiesFor } = useDepartment();
  // What OrderForm calls `userAssignedProductions`: null = admin / still
  // loading, [] = this user holds no assignments at all (the form then blocks
  // submission with an explanation). Computed exactly as OrderPage does it —
  // this is form plumbing, not an authority decision.
  const userAssignedProductions = isAdmin
    ? null
    : (assignments && assignments.length === 0 ? [] : null);
  // Order management is resolved PER ORDER, in the order's own department, the
  // same way the server resolves it. See hooks/useOrderAuthority.js — every
  // `(isAdmin || isBuyer)` gate on this page used to answer a different
  // question from the endpoint it fronted.
  const { canManageOrder } = useOrderAuthority();
  // Shared toast (or a no-op outside the app shell — see useToastOrNoop):
  // carries the received-tick PATCH failures (the line-item
  // rows have no inline message strip, unlike the retired block).
  const { error: toastError, info: toastInfo } = useToastOrNoop();
  const currentRole = isAdmin ? 'admin' : (isBuyer ? 'buyer' : (activeRole || 'user'));
  // A "Stores-only" viewer reaches this page via the Stores capability on a base
  // 'user' role (no buyer/supervisor/lead). They handle goods-in: see the
  // delivery queue + upload paperwork, but get NO approval/ordering actions.
  const isStoresOnly = canManageStores && currentRole === 'user';

  // ── THE PICKERS' REFERENCE LISTS (2026-10-03) ─────────────────────────────
  // 🔑 ONE shared loader with `useMyOrderExpansion` — the latch, both effects,
  // every URL, the `null` supplier sentinel and the empty-department clear all
  // live in `hooks/useOrderPickerLists.js` + `utils/orderReferenceLists.js`.
  // Read the hook's docblock before changing anything here, including the note
  // on why the two effects stay separate (their dependencies differ) and the
  // measurement that settled the two hooks' disagreement about request order.
  // 🔀 `deferReferenceData` (2026-09-27) is still this hook's prop and is
  // passed straight through; `expandedOrders` is what arms it.
  // Request counts are pinned by `DepartmentOrders.expandEdit.test.js` §4.
  // 🚨 ORDER IS LOAD-BEARING: accounting FIRST on this page, reference data
  // second. Hook call order is effect order is REQUEST order, and this page's
  // parity goldens record the recorded call list. Do not reorder these two
  // lines, and do not fold them into one hook — `useOrderPickerLists.js`'s
  // docblock has the measurement (four `MyOrders` golden states went red when
  // one fixed order was tried).
  const referenceArmed = useOrderReferenceArmed({ deferReferenceData, expandedOrders });
  useOrderAccountingLists({
    referenceArmed, activeDepartment, setCostCodes, setPaymentTypes, setLoadFailures,
  });
  useOrderReferenceData({
    referenceArmed,
    setProductions,
    setSuppliers,
    setSubDepartments,
    setGags,
    setSupplierSuggestions,
    setLoadFailures,
  });

  // ── DROP PER-DEPARTMENT EXPAND/EDIT STATE ON A SWITCH (2026-08-17) ────────
  // The list refetch above replaces every visible row, so nothing stale renders
  // once it lands. This clears the transient expand-to-edit view-state at the
  // MOMENT of the switch so that, during the in-flight window, an expanded row
  // cannot keep showing the old department's cached detail, and the poll's
  // `paused` flag (keyed on `editDirtyId`) cannot be
  // left stuck on an editor that is about to unmount. A deliberate switch
  // dropping an in-progress inline edit is ACCEPTABLE — the user navigated away.
  // 🪤 `useRef` seeded with the current id so this is a NO-OP on first render
  // (and on the initial undefined→id resolution): it fires ONLY on a genuine
  // change, so it never double-fetches and never disturbs a single-department
  // session. The clears here trigger no fetch of their own.
  //
  // 🚨 **OWNER RULING, 2026-08-30, verbatim: "yes the department switch should
  // win over the edit, unsaved edits are lost."** This is the ONE place in the
  // order pages where a dirty inline edit is deliberately NOT protected —
  // every neighbouring mechanism does the opposite (collapsing the row you are
  // editing raises a three-way save/discard prompt; the layout toggle that once
  // deferred or asked was retired with the order cards, 2026-10-18). Do not
  // "fix" this effect to match the collapse prompt — his reasoning: the edit belongs to a department the
  // user has just left, so preserving it would leave unsaved work attached to
  // records no longer on screen, which is worse than losing it. ⚠️ **This is
  // ONLY about a DEPARTMENT SWITCH.** Returning to this TAB after it lost focus
  // (`usePollingRefresh`'s tab-refocus refetch) is a DIFFERENT trigger on a
  // DIFFERENT policy and still respects `editDirtyId` via `paused` — see that
  // hook's rule 1. The two triggers look inconsistent side by side on purpose;
  // that is why this comment exists.
  //
  // 🔀 **SUPERSEDED, HOURS LATER, SAME DAY — owner ruling, 2026-08-30, verbatim:
  // "'You have unsaved changes — they'll be lost. Continue / Cancel.' is a good
  // idea, lets have the warning."** The switch still WINS (nothing above this
  // note changes: it never defers or blocks the way collapse does) but it no longer wins SILENTLY — the effect below only runs at
  // all once the user has confirmed a `ConfirmDialog` raised BEFORE the switch,
  // in `components/ProductionDepartmentSwitcher.js` (`handleSelect` →
  // `hasDirtyEdit()` → `pendingDirtySwitch`). **This effect is not where the
  // warning lives and must not gain one of its own** — by the time
  // `activeDepartment?.department_id` has actually changed, the switcher's
  // dialog has already been shown and answered; this effect's job is still
  // only the unconditional clear the first ruling describes. The registration
  // effect immediately below is what makes the switcher's guard see THIS
  // page's dirty state in the first place.
  const prevSwitchDeptRef = useRef(activeDepartment?.department_id);
  useEffect(() => {
    const deptId = activeDepartment?.department_id;
    if (prevSwitchDeptRef.current === deptId) return;
    prevSwitchDeptRef.current = deptId;
    setExpandedOrders(new Set());
    setOrderDetails({});
    setDetailsLoading({});
    setEditDirtyId(null);
    setPendingSaveAction(null);
    setCompletionPrompt(null);
  }, [activeDepartment?.department_id]);

  // Check if user can action a specific order
  const canActionOrder = useCallback((order) => {
    return order.can_action === true;
  }, []);

  // 🔑 THE ONE PREDICATE FOR APPROVE/REJECT — deliberately narrower than
  // `canActionOrder`. The server's `can_action` stays TRUE for a buyer on
  // approved/ordered/partially_delivered orders too (that is what unlocks
  // Mark Ordered — 🔻 this parenthetical also named Partial Delivery / Mark
  // Delivered until 2026-08-16; both are deleted, redundant with the
  // receive-item ticks. 🔀 2026-09-13: "Mark all Delivered" is back as a
  // Next-step item — a shortcut onto that same tick endpoint, not a revival
  // of the deleted manual status items — see the `receive-all` item below),
  // so `canActionOrder` alone let Approve/Reject leak
  // onto orders already past that step —
  // pages/CLAUDE.md's Quick Actions table is explicit: "Approve/Reject
  // (submitted)". Written once here so the inline buttons and the menu
  // items (both panels — read-only and edit-header share one
  // `buildQuickActions` builder below) cannot drift apart. The lifecycle
  // items keep their own `order.status === '…'` conditions unchanged.
  const canApproveReject = useCallback((order) => {
    return canActionOrder(order) && !isStoresOnly && order.status === 'submitted';
  }, [canActionOrder, isStoresOnly]);

  /**
   * 🔑 THE ONE PREDICATE FOR `Skip remaining approvals…` (2026-09-13 owner
   * ruling), and it MIRRORS THE SERVER GATE TERM FOR TERM.
   *
   * `POST /api/approvals/orders/:orderId/override` admits
   * `req.isGlobalAdmin || await canManageDepartmentSettings(req, order.department_id)`
   * — read it, do not take this comment's word for it. So:
   *
   *   · `isAdmin` — the one legitimate site-wide tier, exactly as the server
   *     short-circuits on it before any column is read.
   *   · `capabilitiesFor(order.department_id).canManageDepartmentSettings`,
   *     resolved in THE ORDER'S OWN DEPARTMENT and never the active one. A
   *     department the viewer holds nothing in returns `NO_CAPABILITIES` (`{}`),
   *     so `.canManageDepartmentSettings` is `undefined` — FAILS CLOSED, which
   *     is what `departmentAuthority`'s EMPTY_AUTHORITY does for the same case.
   *   · `=== true`, never truthiness — the house rule for every capability read.
   *
   * 🔴 **NOT `canManageOrder`, AND THE DIFFERENCE IS THE WHOLE POINT.** That
   * hook answers `canManageDepartmentOrders`, which is seeded the same 1 for
   * buyer and supervisor TODAY but is a DIFFERENT COLUMN — and this route
   * deliberately chose the settings capability, because overriding is
   * ADMINISTERING the department's approval workflow rather than taking part in
   * one (`server/routes/approvals.js`'s docblock, and `docs/ROLES.md` §G.5).
   * Widening this to `canManageOrder` reds the other-department case in
   * `requiredActions.manifest.test.js` and
   * `DepartmentOrders.override.test.js` §2.
   *
   * ⚠️ THIS IS A UI CONVENIENCE, NEVER A GATE. Every one of these requests is
   * refused server-side, per department. What it buys is that the item is not
   * offered to somebody who would only collect a 403.
   *
   * The two non-authority terms:
   *   · `status === 'submitted'` — there is nothing to skip on any other
   *     status, and the route answers 404 for an order with no chain.
   *   · `approval_info` present — `GET /api/orders/department` attaches it ONLY
   *     for a submitted order that HAS an `order_approvals` row, so its absence
   *     is exactly "no pending approval to skip". Without this term the item
   *     would appear on a submitted order whose chain was never built, where
   *     the route answers `404 No approval workflow for this order`.
   */
  const canSkipApprovals = useCallback((order) => {
    if (!order || order.status !== 'submitted' || !order.approval_info) return false;
    if (isAdmin) return true;
    return capabilitiesFor(order.department_id).canManageDepartmentSettings === true;
  }, [isAdmin, capabilitiesFor]);

  // 🔀 THE ONE CONTINUATION GUARD (2026-09-28, owner: *"Trying to click on a
  // tab or another row when we have unsaved changes should cause the pop up to
  // show"*) — `continueWhenClean(action)`, from `hooks/useDirtyContinuation.js`.
  // It replaced `blockIfDirty` (refuse + an amber pulse nobody saw) for every
  // control that destroys an open inline edit BY INTENT: expanding another row,
  // the navigating actions and links, and each page's tabs / view switches.
  // Clean → runs now; dirty → the three-way prompt, then the click on Save (ok)
  // or Discard, nothing on Keep editing or a failed save.
  // ⚠️ Still the counterpart to `withDirtyRowPinned`: searching, sorting and
  // paging do not intend to leave the edit, so they are pinned through and
  // never asked about.
  // The held click RE-ENTERS the latest `toggleExpand` through this ref, so it
  // runs against the clean, post-save/discard state.
  const toggleExpandRef = useRef(null);

  // Toggle expand and lazy-load order details.
  //
  // ── Dirty guard (expand-to-edit) — ONE ANSWER SINCE 2026-09-28 ────────
  // An expanded row whose inline edit form has unsaved changes must never be
  // discarded silently. Only one row is open at a time here, so expanding B
  // while A is dirty would discard A's edits just as surely as collapsing A.
  //  - Collapsing THE DIRTY ROW ITSELF asks (since 2026-08-08, owner: *"If we
  //    make a change and try to close collpase the orfer I would Like a pop up
  //    asking to save/discard"*). The prompt lives in `OrderInlineEditor.js`.
  //  - 🔀 Expanding ANOTHER row now asks the SAME prompt and then opens B
  //    (2026-09-28). It was refused with an amber pulse — "nothing may happen to
  //    A" — and the owner, clicking B on staging, saw nothing happen at all.
  const toggleExpand = useCallback((orderId, e) => {
    if (e) e.stopPropagation();
    if (editDirtyId !== null) {
      continueWhenClean(editDirtyId === orderId ? null : () => toggleExpandRef.current(orderId));
      return;
    }
    setExpandedOrders(prev => {
      if (prev.has(orderId)) {
        return new Set();
      } else {
        const next = new Set([orderId]);
        // Fetch details if not already loaded
        if (!orderDetails[orderId] && !detailsLoading[orderId]) {
          setDetailsLoading(dl => ({ ...dl, [orderId]: true }));
          apiGet(`${API_BASE}/api/orders/${orderId}`)
            // W4 fix: a non-ok response (e.g. a stale-department 403) must NOT be
            // handed to `res.json()` and written into the cache — its body would
            // render as though it were the order (see `refreshOneOrder.js`'s
            // "fail to EMPTY, never fail to STALE" rule). Throwing here routes a
            // non-ok response into the same `.catch()` a network exception
            // already takes, which leaves the cache entry ABSENT — exactly what
            // this guard's own `!orderDetails[orderId]` re-fetches on the next
            // expand, and what the row's pre-existing "Failed to load order
            // details." fallback (OrderTableView.js) already
            // renders for.
            .then(res => {
              if (!res.ok) throw new Error(`Failed to load order ${orderId}: ${res.status}`);
              return res.json();
            })
            .then(data => {
              setOrderDetails(od => ({ ...od, [orderId]: data }));
              setDetailsLoading(dl => ({ ...dl, [orderId]: false }));
            })
            .catch(() => {
              setDetailsLoading(dl => ({ ...dl, [orderId]: false }));
            });
        }
        return next;
      }
    });
  }, [orderDetails, detailsLoading, continueWhenClean, editDirtyId]);
  toggleExpandRef.current = toggleExpand;

  // Dirty reporting from an open inline editor, keyed by row id so a closing
  // panel's unmount cleanup can only clear its own flag.
  const handleEditorDirtyChange = useCallback((orderId, isDirty) => {
    setEditDirtyId(prev => (isDirty ? orderId : prev === orderId ? null : prev));
  }, []);

  // Save landed: drop the guard, collapse the row, then refresh BOTH this
  // order's cached DETAIL and the list — in that order. Both are needed; see
  // below for why neither one alone is enough.
  //
  // 🪤 THE DEFECT THIS EXISTS TO CLOSE. Product owner, 2026-08-08, in a real
  // browser: *"changing the supplier and clicking save shows the change in the
  // collapsed table BUT not when I expand it — the fields are editable"*. This
  // handler refreshed the ORDERS LIST and nothing else, so the collapsed row
  // told the truth while `orderDetails[orderId]` still held the PRE-EDIT
  // payload. `toggleExpand` fetches a detail only when the cache is EMPTY
  // (`!orderDetails[orderId] && !detailsLoading[orderId]`), so re-expanding
  // handed the pre-edit order straight back, and the form re-hydrated from it.
  //
  // 🔑 IT IS NOT ONLY STALE TEXT, AND THAT IS WHY THIS IS A FIX AND NOT A
  // POLISH. `showInlineEditor` (at the render site below) gates the unlocked
  // form on `detail.can_edit === true`, read off THIS cache — the whole point
  // of that gate being that the list row's copy goes stale. A stale detail
  // cache silently undermines it on the save path: the editor's
  // open-vs-read-only decision would be taken from a PRE-EDIT answer, which is
  // exactly the "a form opens that the server would then refuse to save" hole
  // the gate closed. `can_edit` is computed PER CALLER by the server and can
  // never be inferred client-side, so it has to come from a round trip.
  //
  // Why `refreshOneOrder` rather than merging the PUT response body: it is the
  // helper the ACTION path already uses (`handleAction`), so there is one way
  // to do this and not two, and it performs a real `GET /api/orders/:id`, whose
  // payload carries a freshly computed `can_edit`. The PUT response does not —
  // `PUT /api/orders/:id` returns `{ ...updated, items }` with no `can_edit`
  // field at all, so merging it could not have fixed the authority half.
  //
  // ⚠️ THE CACHE ENTRY IS DROPPED SYNCHRONOUSLY FIRST, and that is not belt and
  // braces — it is what makes the property hold unconditionally. The refresh is
  // a round trip, so it can FAIL (`refreshOneOrder` returns null and leaves the
  // cache untouched, i.e. still pre-edit) and it can be OVERTAKEN (the user
  // re-expands the row while it is in flight, and `toggleExpand` finds the
  // stale entry present and therefore does not fetch). Deleting the entry up
  // front closes both: a re-expand inside that window finds an empty cache and
  // issues its own fetch through the normal path, which owns its own
  // `detailsLoading` flag — so nothing can strand a spinner either, and the two
  // fetches are both fresh so neither ordering is wrong. `refreshOneOrder` then
  // only pre-warms the cache so a re-expand is instant; the delete is the
  // correctness half. The same invalidate-then-refetch shape already sits on
  // the `restore` action in `handleAction`.
  //
  // ⚠️ SEQUENTIAL, NOT PARALLEL — AND `fetchOrders(true)` DID NOT BECOME
  // REDUNDANT. An edit can change supplier, job name, quantities, unit prices
  // and the item list — i.e. every column of the collapsed row and the whole
  // item preview — and `refreshOneOrder` DELIBERATELY preserves `order_total`,
  // `item_count` and `first_item_*` because the detail endpoint does not
  // compute them. A silent full refetch is still the only thing that leaves the
  // collapsed row telling the truth. Sequencing keeps the full list refetch
  // LAST, so the authoritative payload is the one that settles the row rather
  // than racing the narrow per-order merge.
  //
  // 🪞 THE `can_action` HALF IS NOW FIXED AT SOURCE, NOT WORKED AROUND HERE.
  // This block used to end: *"the real cause is that `refreshOneOrder` clears
  // `can_action` UNCONDITIONALLY … making it opt-out is a change to a shared
  // helper with five other call sites, so it is deliberately NOT made here"*.
  // It has been made. `refreshOneOrder` takes `{ clearCanAction }`, defaulting
  // to `true` so the five ACTION call sites (one in `handleAction` below, four
  // in `MyOrders.js`) are byte-identical, and this — the one caller whose
  // mutation consumes no approval step — passes `false`. The stamp was never a
  // refresh: `can_action` is not on the detail payload at all, so writing it
  // was an ASSERTION, and after an edit it was a false one. The row therefore
  // no longer depends on `fetchOrders(true)` ARRIVING to un-break a button it
  // should never have lost; a slow or failed list refetch now costs the
  // collapsed row's derived columns and nothing else.
  // An attachment was uploaded or removed in the expanded panel. The file list
  // lives on the DETAIL payload, so the component hands back the fresh
  // `GET /api/orders/:id` body and the page writes it into the detail cache.
  //
  // 🔑 THE WHOLE PAYLOAD REPLACES THE CACHE ENTRY, not a `files`-only merge —
  // and it deliberately does NOT touch the list row. CLAUDE.md's rule for this
  // page is that *every* mutation path must refresh the DETAIL cache, which is
  // what went wrong when `handleEditorSaved` refreshed only the list and a
  // re-expand served a pre-edit payload (with a stale `can_edit` in the same
  // breath). Attaching a file changes nothing the collapsed row renders —
  // no total, no item count, no status — so a `fetchOrders(true)` here would be
  // a whole-list refetch bought for nothing, and on this page it would also
  // race the 30s poll. If a future column ever surfaces an attachment count,
  // this is the handler that has to start refreshing the row too.
  // 🔻 THAT DAY CAME (the paperclip, `file_count`, 2026-08-11) AND THE ROW
  // WAITED FOR THE POLL — so the ATTACHMENTS panel now goes through
  // `handleAttachmentsChanged` below, which refreshes the list. This callback
  // stays detail-only because the received ticks use it too, and they already
  // refresh the list through `onListRefresh`.
  // (`handleOrderFilesChanged` now comes from `useExpandedOrderCore` — the
  // Department Orders and My Orders copies were byte-identical.)
  // The attachments panel's `onFilesChanged`: the detail write above, plus —
  // on a hires page only — the "an order changed" report, because the hires
  // row carries `file_count` (its PO/Order paperclip) and the detail write
  // cannot reach it (2026-09-29). Kept apart from `handleOrderFilesChanged`,
  // which the received ticks also use and which already refresh the list.
  const handleAttachmentsChanged = useCallback((orderId, freshDetail) => {
    handleOrderFilesChanged(orderId, freshDetail);
    if (!orderId || !freshDetail) return;
    // A hires page refetches its hire rows; an order page its order list.
    if (onOrderMutated) onOrderMutated();
    else requestListRefresh();
  }, [handleOrderFilesChanged, onOrderMutated, requestListRefresh]);

  // THE STATUS OVERRIDE's `onSaved` (`InlineStatusJump`). `mergeStatusJumpResult`
  // merges `status` / `rejection_reason` into the row and the detail; two things
  // it cannot reach are fixed here (owner, 2026-09-29, "fix all"): `can_action`
  // (the green chevron, the Actionable queue) is computed only by the LIST, so
  // the list is refetched; and the To Approve badge / sidebar dots listen for
  // `sfx-approval-change`, which Approve and Reject already dispatch and a jump
  // into or out of `submitted` changes just as much. On a hires page the
  // notifying `setOrders` has already asked for the hires refetch.
  const handleStatusJumpSaved = useCallback((updated) => {
    mergeStatusJumpResult(updated, setOrders, setOrderDetails);
    window.dispatchEvent(new Event('sfx-approval-change'));
    if (!onOrderMutated) requestListRefresh();
  }, [setOrders, onOrderMutated, requestListRefresh]);

  // 🔴 W1 close-out (2026-08-27) — `utils/receivedItems.js` calls this when a
  // tick/receive-all PATCH SUCCEEDS but the follow-up `GET /api/orders/:id`
  // refresh does not (either shape: a rejected `apiGet` or a resolved non-ok
  // response). "Fail to EMPTY, never fail to STALE": the entry is DELETED,
  // not left holding whatever it held before the mutation, so `toggleExpand`'s
  // `!orderDetails[orderId] && !detailsLoading[orderId]` guard re-fetches on
  // the next re-expand and the row's pre-existing "Failed to load order
  // details." fallback (`OrderTableView.js` — not new, and
  // not part of W4; it already existed for a thrown/rejected fetch) covers the
  // gap in between. Same delete shape `handleEditorSaved` already uses below.
  //
  // 🚨 DELIBERATELY NOT `handleOrderFilesChanged`. That callback is a hard
  // no-op on a falsy `freshDetail` — correct for ITS job (an attachment
  // upload's success handler must never overwrite a good cache entry with
  // nothing), but exactly why it CANNOT double as an "invalidate this entry"
  // signal: calling it with `null` here would silently do nothing and leave
  // the stale entry in place, which is the bug this closes. Two different
  // callbacks for two different claims about the cache — do not merge them.
  // (Both now live in `useExpandedOrderCore`, which carries this reasoning.)

  // ── RECEIVED TICKS IN THE LINE-ITEM ROWS (2026-08-15) ────────────────────
  // Product owner: *"I would like the tick boxes to go in the collpased line
  // items in the 'Line items' section maybe between the price and the actions
  // menu so they can be ticked off without haveing to expand the order. This
  // will save the whole Items received section."* The standalone block in
  // `OrderExpandedDetails` is retired; the SAME mutations (per-item PATCH,
  // bulk receive-all, detail-then-list refresh) now run from the page through
  // `makeReceivedItemHandlers` — ONE shared implementation with `MyOrders.js`,
  // not a copy. Errors go to the shared toast (the rows have no message strip
  // of their own); `receivingOrderId` disables that ORDER's ticks while a
  // PATCH is in flight, like the old block's `receiving` state.
  const [receivingOrderId, setReceivingOrderId] = useState(null);
  const receivedHandlers = useMemo(() => makeReceivedItemHandlers({
    onDetailRefreshed: handleOrderFilesChanged,
    onDetailInvalidated: handleOrderDetailInvalidated,
    onListRefresh: handleItemsReceived,
    onBusyChange: (orderId, busy) => {
      setReceivingOrderId(busy ? orderId : null);
      if (onTickBusyChange) onTickBusyChange(orderId, busy);
    },
    onError: (err) => toastError(err),
  }), [handleOrderFilesChanged, handleOrderDetailInvalidated, handleItemsReceived, onTickBusyChange, toastError]);

  const handleEditorSaved = useCallback((orderId) => {
    setEditDirtyId(null);
    setExpandedOrders(new Set());
    setOrderDetails(od => { const copy = { ...od }; delete copy[orderId]; return copy; });

    // 🚨 SAVE-THEN-ACT SHORT-CIRCUIT (2026-08-09) — the twin of the one on
    // `Approvals.js`. When this save was requested BY a row action
    // (`saveThenRun`), that action owns the messaging and runs its OWN
    // `refreshOneOrder` afterwards. Running the cascade here as well would
    // RACE it: two refreshes of the same row in flight, and the older payload
    // can land last and undo the action's result on screen.
    if (actionAfterSaveRef.current === orderId) return;

    setSuccessMessage('Order updated.');
    setTimeout(() => setSuccessMessage(''), 3000);
    refreshOneOrder(orderId, setOrders, setOrderDetails, { clearCanAction: false })
      .then(() => fetchOrders(true));
  }, [fetchOrders, setOrders, setSuccessMessage]);

  // ── THE ROW-ACTION DOUBLE-SUBMIT GUARD (2026-08-13) ──────────────────────
  // `hooks/useSubmitGuard.js` — the OrderForm guard, shared. Before this the
  // page had NO disabled state on its action controls at all, and a fast
  // double-click on Approve fired `POST .../approve` twice: the first advances
  // `current_step`, so the second approves the NEXT step — the Approve
  // double-step bug. The ref inside `guard` drops the second call in the same
  // tick; `actionBusy` disables BOTH copies of every mutating control (the
  // inline Approve/Reject buttons AND their `Actions ▾` menu items — the owner
  // ruled the two surfaces, so both must disable together).
  // ONE gate for the whole row-action set on purpose: every mutating action
  // ends in a refetch of the very list the other buttons act on, so a second
  // action while one is in flight is acting on rows about to change.
  // NAVIGATING actions (`view`/`edit`/`upload`) pass through the guard too —
  // harmless (they release synchronously) and it keeps one call path.
  const { guard: guardAction, busy: actionBusy } = useSubmitGuard();
  // One attempt-identity for the `duplicate` create — reused on retry of a
  // failed attempt, cleared on success (see `hooks/useIdempotencyKey.js`).
  const duplicateIdem = useIdempotencyKey();

  // 🔀 2026-10-03 — THE IN-PAGE REASON DIALOG replaced the two `window.prompt`
  // calls this handler used to make. `requestOrderReason`,
  // `requestRejectionReason` and the `reasonDialog` node all come from
  // `useExpandedOrderCore` above, shared with `useMyOrderExpansion` — they were
  // written identically in both hooks for one commit and
  // `npm run check-duplication` caught it (keys 60dc2d02, c2e04917).
  // 🔑 It resolves `null` on dismiss and a string on Confirm, exactly what
  // `window.prompt` answered, so every `if (reason === null) return;` below is
  // unchanged.

  // Action handlers - only approval actions and cancel for supervisors
  // 🔀 `opts` (2026-09-14) carries the approve-on-behalf `reason` from the
  // dialog's Confirm. Only that path passes it; the plain own-step Approve
  // passes nothing and its body is byte-identical.
  const handleAction = useCallback((orderId, action, order, opts = {}) => guardAction(async () => {
    try {
      if (action === 'approve') {
        // `expected_step_order`: the step this button was RENDERED against,
        // from the list row's `approval_info`. The server treats it as an
        // OPTIONAL precondition (absent → log-and-allow), so a row that
        // cannot cleanly name its step sends nothing rather than guessing.
        const renderedStep = order?.approval_info?.current_step;
        const res = await apiFetch(`${API_BASE}/api/approvals/orders/${orderId}/approve`, {
          method: 'POST',
          body: JSON.stringify({
            notes: '',
            ...(Number.isInteger(renderedStep) ? { expected_step_order: renderedStep } : {}),
            // Approve on behalf: `400 approve_on_behalf_reason_required` without it.
            ...(opts.reason ? { reason: opts.reason } : {}),
          }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to approve');
        }
        setSuccessMessage('Order approved successfully!');
        // Owner 2026-10-11: an approved order folds away — the decision is
        // made, so the list is what the approver wants next. (To Approve
        // already drops the row; this is the Department Orders half.)
        setExpandedOrders(new Set());
        window.dispatchEvent(new Event('sfx-approval-change'));
      } else if (action === 'reject') {
        const reason = await requestOrderReason(order, 'reject');
        if (reason === null) return;
        const res = await apiFetch(`${API_BASE}/api/approvals/orders/${orderId}/reject`, {
          method: 'POST',
          body: JSON.stringify({ notes: reason || 'Rejected' }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to reject');
        }
        setSuccessMessage('Order rejected.');
        window.dispatchEvent(new Event('sfx-approval-change'));
      } else if (action === 'ordered') {
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/status`, {
          method: 'PATCH',
          body: JSON.stringify({ status: 'ordered' }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to mark as ordered');
        }
        setSuccessMessage('Order marked as ordered.');
      // 🔻 **`partially_delivered` AND `delivered` BRANCHES ARE DELETED
      // (2026-08-16), WITH THE TWO MENU ITEMS THAT CALLED THEM.** Owner:
      // *"I tink we should have the 'mark as delivered' tick box avaliable
      // until the order is marked as ordered … Also remove the discussed
      // items from the action menu"* — "discussed items" = Partial Delivery
      // + Mark Delivered. They were a MANUAL status set via
      // `PATCH /:id/status`, redundant with the receive-item endpoints
      // (`PATCH /:id/items/:itemId/received` + `…/items/receive-all`), which
      // ALREADY recompute this same status from the ticks (some → partially
      // delivered, all → delivered — see their comments in
      // `server/routes/orders.js`). Ticking items is now the ONLY route to
      // either status from this page. `ordered` above is UNCHANGED and
      // KEPT — there is no tick-derived route onto it, so the manual action
      // stays the only way in.
      } else if (action === 'complete') {
        // Order Completion (2026-08-16) — the ConfirmDialog gate has already
        // run (see `completionPrompt` below) BEFORE this is reached; the
        // server enforces status + authority only (no paperwork/received
        // check), matching `PATCH /api/orders/:id/complete`.
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/complete`, {
          method: 'PATCH',
          body: JSON.stringify({ complete: true }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to mark order complete');
        }
        setSuccessMessage('Order marked complete.');
      } else if (action === 'reopen') {
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/complete`, {
          method: 'PATCH',
          body: JSON.stringify({ complete: false }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to re-open order');
        }
        setSuccessMessage('Order re-opened.');
      } else if (action === 'cancel') {
        const reason = await requestOrderReason(order, 'cancel');
        if (reason === null) return;
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/cancel`, {
          method: 'PATCH',
          body: JSON.stringify({ cancellation_reason: reason || undefined }),
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to cancel order');
        }
        setSuccessMessage('Order cancelled.');
      // 🔻 **THE `restore` BRANCH IS DELETED (2026-08-12).** Leaving `cancelled`
      // is the status override's job on THIS page now; see the deleted `Restore`
      // menu item below for the gate reasoning.
      //
      // ⚠️ **IT WAS *NOT* THE ONLY CLIENT CALLER OF
      // `PATCH /api/orders/:id/uncancel`, AND AN EARLIER VERSION OF THIS COMMENT
      // SAID IT WAS. THREE MORE SURVIVED, ON TWO OTHER PAGES** — `MyOrders`'s
      // own `Restore Order` button, offered to the order's OWNER, and
      // `OrderPage`'s mobile and desktop copies, both gated
      // `isAdmin || order.requested_by === localStorage 'sfx_requested_by'`,
      // a DISPLAY-NAME match that was itself a READ-SCOPE finding in
      // `docs/ROLES.md` §I.
      // 🟢 **ALL THREE, AND THE ROUTE ITSELF, ARE NOW DELETED (2026-08-12).**
      // The name-as-identity row is RETIRED rather than re-cited, because the
      // sites are gone rather than fixed.
      // 🔑 **The caution above still stands: that removed the reachable-by-a-
      // user paths, NOT the resurrection SHAPE** —
      // `POST /api/backup/department/restore` can still write the inconsistent
      // pair. The `'rejected'` exclusion in both To Approve queries closes it.
      // 🪤 The three sites above were cited `MyOrders.js:326` (which is the
      // CANCEL branch, not restore) and `OrderPage.js:800`/`:889`, and
      // `docs/ROLES.md:1746` — a file 1,689 lines long, so that one pointed
      // past EOF. Line numbers in prose here were wrong more often than right;
      // cite by SYMBOL, not by number.
      } else if (action === 'view') {
        navigate(`/orders/${orderId}`);
        return;
      } else if (action === 'duplicate') {
        // A protected CREATE: send the attempt's Idempotency-Key (minted on
        // arm, reused on a retry of a failed attempt, cleared on success).
        const res = await apiFetch(`${API_BASE}/api/orders/${orderId}/duplicate`, {
          method: 'POST',
          headers: { 'Idempotency-Key': duplicateIdem.mint() },
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to duplicate order');
        }
        duplicateIdem.clear();
        const data = await res.json();
        setSuccessMessage(`Order duplicated! New order: ${getOrderNumber(data) || 'Created'}`);
      } else if (action === 'upload') {
        navigate(`/orders/${orderId}?upload=1`);
        return;
      }
      setTimeout(() => setSuccessMessage(''), 3000);
      // Optimistic in-place refresh: only re-fetch the actioned order so we
      // keep scroll position + expanded state. Duplicate is the exception —
      // it creates a *new* order so we need a full list refresh.
      if (action === 'duplicate') {
        fetchOrders();
      } else {
        // 🚨 THE STALE-APPROVE-BUTTON BUG (2026-08-27, reported from
        // production: *"ive approved the order, but it still allows me to
        // approve when i go onto the order"*, while To Approve correctly
        // showed nothing pending). Root cause: `canApproveReject` and every
        // status-lifecycle gate in `buildQuickActions` read the LIST row
        // (`order.status` / `order.can_action`), and until this change the
        // ONLY thing that could correct that row after an action was a
        // single `refreshOneOrder` — one `GET /api/orders/:id` that CATCHES
        // its own failure and returns `null` (`utils/refreshOneOrder.js`),
        // leaving `setOrders`/`setOrderDetails` completely untouched. A
        // dropped connection on that one request left the row — status chip
        // AND Approve/Reject — silently wrong, with the approval already
        // recorded server-side and nothing on screen saying the refresh had
        // failed.
        //
        // 🪤 AND IT COULD NOT SELF-HEAL BY RE-EXPANDING. Approve/Reject only
        // render once `detail` is loaded (`buildQuickActions` is gated
        // `detail ? … : null`), so by the time this fires the row's detail
        // cache is already populated — and `toggleExpand`'s
        // `!orderDetails[orderId] && !detailsLoading[orderId]` guard only
        // fetches when that cache is EMPTY. A stale-but-present entry is
        // therefore served forever on every collapse/re-expand, exactly the
        // shape `handleEditorSaved` (§12 above) was fixed for on the SAVE
        // path — this is the same hole on the ACTION path.
        //
        // `refreshOrderRow` (`utils/refreshOneOrder.js`) is the shared fix:
        // drop the cached detail entry SYNCHRONOUSLY first — fail to EMPTY,
        // never fail to STALE, so a failed refresh leaves the cache empty
        // (which `toggleExpand` already knows how to recover from) rather
        // than replaying the pre-action payload forever — then
        // `refreshOneOrder` to pre-warm both caches on the common case.
        // 🔀 **THE LIST REFETCH IS CONDITIONAL, NOT UNCONDITIONAL — CORRECTED
        // 2026-08-27, MINUTES AFTER THIS FIX FIRST REACHED STAGING.**
        // `e2e/online-order-action-no-refetch.spec.js` (`@smoke`, staging-only
        // — invisible to the client suite) pins a deliberate cost contract: a
        // single-row action refreshes only that row, never the whole list.
        // Firing `fetchOrders(true)` on every action, success included, broke
        // that on the overwhelmingly common (successful) case. `refreshOrderRow`
        // now fires the list refetch ONLY when `refreshOneOrder` returned
        // falsy — see its own docblock for why that condition is sound and why
        // it does NOT generalise to the cache-delete step above, which must
        // stay unconditional. Every ACTION call site on this page and on
        // `MyOrders.js` goes through this one function now.
        await refreshOrderRow(orderId, { setOrders, setOrderDetails, fetchList: () => fetchOrders(true) });
      }
    } catch (err) {
      // 🔧 FIX ROUND 1, finding 2 (adversarial review, 2026-09-02): a
      // previous pass CLAIMED this was already changed (design doc §5) and
      // it was not — the doc was wrong, corrected there in the same
      // commit as this fix. `window.alert` was the one path the new
      // duplicate-refusal message (`duplicate_source_mixed_hire`, minted a
      // few lines above at the `throw new Error(data.error)` for the
      // `duplicate` branch) would have travelled through, showing the user
      // a raw error token in a browser alert — the exact defect this repo
      // already fixed once, hours earlier, in the extend dialog
      // (`adjustments_exist`). `toastError` is already imported (`:60`,
      // bound at `:288`) and already used elsewhere in this file (`:939`,
      // `:1756`); it accepts the whole Error object and routes it through
      // `friendlyErrorMessage` itself, so no other change is needed here.
      toastError(err);
    }
  }), [currentUser, currentRole, navigate, fetchOrders, guardAction, duplicateIdem, toastError, setOrders, setSuccessMessage]);

  /**
   * ── SAVE THE CURRENT STATE, *THEN* ACT (2026-08-09) ───────────────────
   *
   * The twin of `saveThenRun` on `Approvals.js`, and the product owner's ruling
   * governs this page too: *"approve butotn should approve and save the current
   * state"*. Here it covers the whole status lifecycle, not only Approve —
   * marking an order Delivered while corrected quantities sit unsaved on screen
   * is the same ambiguity wearing a different verb.
   *
   * 🚨 **A FAILED SAVE MUST NEVER ACT, AND IT IS STRUCTURAL.**
   * `editorSaveRef.current()` resolves `true` only after a `res.ok` PUT (see
   * `OrderInlineEditor.js`), and `run()` is reached from exactly one place:
   * inside that `true` branch. A refused PUT, or a form that failed its OWN
   * validation and so issued no PUT at all, cannot reach the action.
   *
   * 🟢 A CLEAN form goes straight through — no pointless PUT, because an empty
   * diff still writes an `order_audit_log` row.
   *
   * 🔀 **AND SINCE 2026-08-10 THE SAVE IS EXPLICIT: A DIRTY ROW ASKS FIRST.**
   * Product owner: *"if there are unsaved changes and the user clicks approve
   * lets have a popup saying 'un saved changes!' then offer buttons like 'save
   * and approve' 'cancel' cancel doesnt undo the changes it just stops the
   * approval"*.
   * 🚨 **THAT IS A REFINEMENT OF HIS 2026-08-09 RULING, NOT A REVERSAL — do not
   * "restore" the silent save as a bug fix.** The action still applies to the
   * SAVED order and a failed save still cannot act; what changed is that the
   * write is announced and declinable. Both quotes and every copy decision are
   * in `constants/saveThenActPrompt.js`.
   * 🔑 **THE PROMPT IS A GATE IN FRONT OF THE MACHINERY BELOW, NOT A REPLACEMENT
   * FOR IT.** `saveThenRun` now only DECIDES; `performSaveThenRun` is the old
   * body, verbatim, and is the only thing that can reach `run()`.
   */
  const editorSaveRef = useRef(null);
  const actionAfterSaveRef = useRef(null);

  /**
   * 🚨 **THE FAILED-SAVE-CANNOT-ACT GUARANTEE LIVES HERE AND IS STRUCTURAL.**
   * `run()` appears in exactly ONE place in this function — inside the `saved`
   * branch — and `saveThenRun`'s clean-form short-circuit is the only other
   * `run()` call on this page. There is no `catch` that has to remember not to
   * act, and the new dialog added no third path: `Keep editing` returns without
   * ever calling this function.
   */
  const performSaveThenRun = useCallback(({ orderId, run }) => {
    const requestSave = editorSaveRef.current;
    // The editor unmounted between the click and the confirmation (a poll
    // refresh, a collapse). Nothing to save and nothing to act on.
    if (!requestSave) return;
    actionAfterSaveRef.current = orderId;
    requestSave().then((saved) => {
      actionAfterSaveRef.current = null;
      if (saved) run();
      // Otherwise the row stays open showing the editor's own error banner or
      // the form's field errors, the edits are intact, and nothing was actioned.
    });
  }, []);

  /**
   * 🟢 **A CLEAN FORM STILL ACTIONS WITH NO PUT *AND NO PROMPT*.** Both halves
   * matter: a dialog on a form nobody edited is a confirmation for nothing,
   * which is how confirmations stop being read.
   * ⚠️ **The predicate is `editDirtyId !== orderId`, not "is anything dirty".**
   * A dirty row elsewhere on the page does not make THIS row's action a save —
   * it never did, and actioning an unrelated row must not raise a dialog about
   * edits that are not on it.
   */
  const saveThenRun = useCallback((orderId, kind, run) => {
    if (editDirtyId !== orderId || !editorSaveRef.current) { run(); return; }
    setPendingSaveAction({ orderId, kind, run });
  }, [editDirtyId]);

  /**
   * THE ONE ENTRY POINT for a row action fired from the expanded panel.
   *
   * 🔑 It splits on what the action DOES to an open edit, which is not the same
   * split as "does it write to the server":
   *   - **NAVIGATING** (`view` / `edit` / `upload`) unmounts the page, so it
   *     destroys the edit BY INTENT — that is the page's existing block-and-
   *     pulse case, exactly like the row title link. 🔻 It was also exactly
   *     like the editor's own "Open full order page →" link until 2026-09-13,
   *     when the owner had that link deleted as redundant with `View Details`.
   *     **`view` being in this list is what made that deletion safe** — the
   *     menu item inherits the identical guard, so no coverage was lost.
   *   - **MUTATING** (everything else) applies to the order, so it saves first.
   * ⚠️ `reject` and `cancel` collect their reason/confirmation
   * through `window.prompt`/`confirm` INSIDE `handleAction`, i.e. after the
   * save. Dismissing that prompt therefore leaves the edit saved — which is the
   * user's own reviewed work committed, not work lost, and is the lesser of the
   * two costs against prompting before a save that may then fail.
   * 🟢 **THAT COST SHRANK ON 2026-08-10 and the note is kept because the
   * SEQUENCE is what surprises people.** With the unsaved-changes prompt in
   * front, the order is: our dialog → the PUT → the native reason prompt. So a
   * user who dismisses the native prompt has still had the save announced and
   * has agreed to it; the edit being committed is now something they asked for
   * rather than something that happened to them.
   *
   * 🚨 **EVERY MUTATING ACTION PROMPTS, NOT ONLY APPROVE, AND THAT IS
   * DELIBERATE.** The owner named Approve; marking an order Delivered while
   * corrected quantities sit unsaved on screen is the same unannounced write
   * wearing a different verb. **Approve-only is not a cautious middle — it is
   * the worst of the three options**, because one button asking while six do not
   * is an inconsistency the next reader will "fix" in whichever direction they
   * guess. `constants/saveThenActPrompt.js` carries a label for each and a test
   * that fails if an action is added without one.
   */
  const NAVIGATING_ACTIONS = ['view', 'upload'];
  const runRowAction = useCallback((orderId, action, order, opts) => {
    if (NAVIGATING_ACTIONS.includes(action)) {
      // Asks first when dirty, then navigates (2026-09-28 — was refuse+pulse).
      continueWhenClean(() => handleAction(orderId, action, order));
      return;
    }
    // `opts` rides inside the held closure, so a dirty row's save prompt
    // replays the approve WITH its on-behalf reason (2026-09-14).
    saveThenRun(orderId, action, () => handleAction(orderId, action, order, opts));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [continueWhenClean, handleAction, saveThenRun]);

  // 🔀 EXTRACTED 2026-10-03 INTO `hooks/useExpandedOrderCore.js`, which My
  // Orders and Approvals call too: `updateOrderListFromItem`,
  // `handleCostCodeChange`, `handlePaymentTypeChange`,
  // `handleItemNotifyUpdated`, `handlePoNumberSaved`,
  // `handleExpectedDeliverySaved` and `handlePoke`. The reasoning moved with
  // them — why the cache is hand-patched instead of refetched, and why the two
  // accounting selects are now one `patchItemField`.

  // Approve-on-behalf gate (2026-08-18) — the ONE place every Approve
  // trigger on this page funnels through before the request fires. `opts.direct`
  // routes to `handleAction` (bypassing save-then-act) for the Override
  // quick-action, matching what it did before this change (a native
  // `window.confirm`, now this dialog); everything else goes through
  // `runRowAction`, unchanged. Compares on `approval_info.approver_user_id`,
  // never on the name — see `utils/orderStepInfo.js#isApprovingSomeoneElsesStep`.
  const requestApprove = useCallback((order, opts = {}) => {
    if (isApprovingSomeoneElsesStep(order, currentUserId)) {
      setApproveOnBehalfReason('');
      setApproveOnBehalfPrompt({ order, direct: !!opts.direct });
      return;
    }
    if (opts.direct) {
      handleAction(order.id, 'approve', order);
    } else {
      runRowAction(order.id, 'approve', order);
    }
  }, [currentUserId, handleAction, runRowAction]);

  // ─────────────────────────────────────────────────────────────────────────
  // SKIP REMAINING APPROVALS (2026-09-13 owner ruling) — TWO DOORS, ONE DIALOG
  // ─────────────────────────────────────────────────────────────────────────
  // Owner: *"the override should require a reason"*, refining his 2026-08-19
  // *"make the jump demand a reason"*.
  //
  // 🔑 `requestSkipApprovals` is the ONLY place `overridePrompt` is set, and it
  //    takes an `onResolve` callback rather than doing the work itself —
  //    because the two callers need different things from the same dialog:
  //      · the `Skip remaining approvals…` MENU ITEM wants the reason handed to
  //        `performOverride` (`POST /api/approvals/orders/:id/override`);
  //      · `InlineStatusJump` is mid-`await` inside its own handler and wants
  //        the reason RETURNED, so `requestStatusJumpReason` wraps this in a
  //        Promise and resolves it.
  //    One dialog, one wording, two continuations. A second `useState` + a
  //    second dialog would be the copy this repo's Code Unification Principle
  //    forbids, and the wordings would drift within a month.
  //
  // 🪤 `onResolve` receives the TRIMMED reason, or `null` on cancel. Never `''`:
  //    a caller testing `if (reason)` must not be able to proceed on a blank.
  const requestSkipApprovals = useCallback((order, { targetStatus, onResolve } = {}) => {
    setOverrideReason('');
    setOverridePrompt({ order, targetStatus, onResolve });
  }, []);

  // The MENU ITEM's continuation. Guarded like every other row action (one
  // in-flight mutation per row), and it refreshes through the SAME
  // `refreshOrderRow` every action on this page uses — cache delete first, then
  // `refreshOneOrder`, with the list refetch only on failure.
  // 🔑 `sfx-approval-change` is dispatched for the same reason Approve and
  //    Reject dispatch it: the To Approve badge in the sidebar counts pending
  //    steps, and this has just resolved every one of them.
  const performOverride = useCallback((order, reason) => guardAction(async () => {
    try {
      const res = await apiFetch(`${API_BASE}/api/approvals/orders/${order.id}/override`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to skip the remaining approvals');
      }
      await refreshOrderRow(order.id, { setOrders, setOrderDetails, fetchList: () => fetchOrders(true) });
      window.dispatchEvent(new Event('sfx-approval-change'));
      toastInfo(`Approvals skipped for ${getOrderNumber(order)}.`);
    } catch (err) {
      // `toastError` takes the whole Error and routes it through
      // `friendlyErrorMessage` itself — never `window.alert`, which is how a raw
      // error token reached a user once already on this page.
      toastError(err);
    }
  }), [guardAction, fetchOrders, toastError, toastInfo, setOrders, requestOrderReason]);

  // The STATUS-DROPDOWN's door. `InlineStatusJump` asks for a reason only when
  // the SERVER has said one is required (`400 status_jump_reason_required`) —
  // see that component for why it reacts rather than predicts.
  const requestStatusJumpReason = useCallback((order, targetStatus) => new Promise((resolve) => {
    requestSkipApprovals(order, { targetStatus, onResolve: resolve });
  }), [requestSkipApprovals]);

  const handleNextStepClick = async (order) => {
    const stepInfo = getNextStepInfo(order, { canAction: canActionOrder(order), role: currentRole, userName: currentUser });
    if (!stepInfo.action) return;
    if (stepInfo.action === 'approve') {
      requestApprove(order, { direct: true });
    }
  };

  // The row's next-step answer, read by the expanded panel's quick actions AND
  // by the calling page's own row nodes — one expression, two readers.
  const stepInfoFor = useCallback(
    (order) => getNextStepInfo(order, { canAction: canActionOrder(order), role: currentRole, userName: currentUser }),
    [canActionOrder, currentRole, currentUser]
  );

  /**
   * THE EXPANDED ROW for one list row — the `expandedContent` node the page
   * hands to whichever row component is active. The body below is the page's
   * per-row block, moved verbatim.
   */
  const renderExpanded = (order) => {
              const detail = orderDetails[order.id];
              const stepInfo = stepInfoFor(order);


              // 📦 RECEIVED GATE + DELIVERY-STAGE PREDICATE — resolved ONCE per
              // row, up here, because THREE consumers below read them: the
              // read-only line-items block (ticks in the rows), the edit
              // branch's `received` prop on OrderInlineEditor, and
              // `canUploadDelivery` for the expanded-details panel. The server
              // mirror is documented at the `canUploadDelivery` site below.
              // Delivery-stage gates on the DATA (`detail.status`) via the
              // SHARED constant, so it cannot drift from the server's own
              // received gate — a `submitted` order never shows a tick.
              const isOrderOwner = !!detail && currentUserId !== '' && String(detail.user_id) === String(currentUserId);
              const canReceiveItems = !!detail && (isAdmin || canManageOrder(detail) || isOrderOwner || capabilitiesFor(detail.department_id).canManageStores === true);
              const isDeliveryStage = !!detail && DELIVERY_STAGE_STATUSES.includes(detail.status);
              const showReceivedTicks = canReceiveItems && isDeliveryStage;
              // 📦 May a line be received on this order's STATUS
              // (RECEIVE_ENABLED_STATUSES, `constants/statuses.js`). 🔀 Since
              // 2026-10-28 (ruling A) the gate is per LINE — a line offers
              // "Delivered" once THAT line is ordered (`deliveredControlState`) — so this
              // is approved-or-later and the 2026-08-16 order-level greying is
              // gone. Resolved once here, on the DATA.
              const canReceiveNow = !!detail && RECEIVE_ENABLED_STATUSES.includes(detail.status);
              const receivingThisOrder = receivingOrderId === order.id;
              // ⚖️ GOODS ONLY (owner ruling 2026-09-19) — an adjustment line
              // is a financial line, not something anybody receives.
              // 🔀 This five-line block was BYTE-IDENTICAL to the one in
              // `MyOrders.js` and both now call the shared helper. They were
              // written together and agreed by luck; the ruling had to change
              // both, which is exactly the moment two copies drift.
              const { received: receivedCount, ordered: orderedCount, total: receivableCount } =
                countDelivery(detail && detail.items);
              const allItemsReceived = !!detail && allGoodsReceived(detail.items);
              // 🔀 PER-LINE "ORDERED" (2026-10-28) — who may tick a line ORDERED
              // is the server's `requireOrderManagement` (order management in
              // the order's OWN dept, or the My Space owner arm — both inside
              // `canManageOrder`), NARROWER than `canReceiveItems`: Stores and
              // the requester may receive but not order. Resolved here, once.
              const canOrderItems = !!detail && (isAdmin || canManageOrder(detail));
              // "Mark all Delivered" only while some line is ordered and not yet
              // received (receive-all receives only ORDERED lines).
              const someAwaitingDelivery = orderedCount > receivedCount;

              // Expanded panel content (DepartmentOrders: line item editor + status action buttons)
              //
              // 🔀 SPLIT IN TWO ON 2026-08-09 — `readOnlyPanelBody` (metadata,
              // accounting, line items) and `quickActionsRow` below. The row is
              // its own const because the EDIT branch has to render it too; see
              // the block comment at its definition.
              // ════════════════════════════════════════════════════════════════
              // THE THREE PANEL CONTROLS THE EDIT FORM DOES *NOT* REPLACE
              // ════════════════════════════════════════════════════════════════
              // 🔴 The `? :` over the whole panel discarded the ENTIRE read-only
              // branch whenever the editor won, so a buyer on an editable order
              // in the table layout lost every control in it. Most of them the
              // form genuinely supersedes — it has its own PO Number, Job/Rig,
              // Notes and line-item fields, and rendering a second copy beside
              // it would be a worse defect than the one being fixed (two PO
              // inputs on one panel that can disagree).
              //
              // 🔑 **THESE THREE HAVE NO COUNTERPART IN `OrderForm` AT ALL**, so
              // for them the ternary was pure loss:
              //   - the STATUS JUMP, which is also the only route to **Cancel**
              //     (the standalone Cancel button was removed 2026-05-28 in
              //     favour of this dropdown's `cancelled` option);
              //   - the REJECTION REASON, which is why the order came back and
              //     the single most relevant thing on screen when it did;
              //   - ADMIN NOTES, read and write.
              //
              // 🚨 **EVERY GATE BELOW IS VERBATIM WHAT IT WAS — `canManageOrder`
              // resolved in the ORDER'S OWN department, written beside each
              // control.** They are hoisted, not rewritten: not merged into one
              // condition, not hoisted to page level, and emphatically not
              // re-derived from any page-level `isAdmin`/`isBuyer` (the unscoped
              // flag `OrderPage.js:44` folds in and this panel deliberately does
              // not). Moving JSX must grant nobody anything.
              // 🔀 **MOVED INTO THE ACTIONS MENU'S FOOTER ON 2026-08-10** — the
              // product owner listed *"Status Override: Submitted (current)"*
              // among the things the single actions menu replaces.
              // 🔑 **IT IS THE MENU'S FOOTER AND NOT ONE OF ITS ITEMS, AND THAT
              // IS THE WHOLE OF THE DESIGN HERE.** Every row above it FIRES an
              // action and dismisses; this is a `<select>` you SET, with its own
              // confirm-and-prompt flow (backward jumps warn about clearing
              // item-delivered flags; `rejected` prompts for a reason;
              // `cancelled` delegates to the page's cancel form). Rendering it
              // as menu items would mean reimplementing that flow a second time
              // — which is what `InlineStatusJump`'s own docblock exists to
              // prevent — and a control that looks like the buttons above it but
              // does not behave like them is worse than either.
              // 🚨 **THE GATE AND THE COMPONENT ARE VERBATIM.** Same
              // `canManageOrder(detail)` resolved in the ORDER'S OWN department
              // (server gate: `PATCH /api/orders/:id/status`), same `testId`,
              // same handlers. This moves JSX; it grants nobody anything.
              // ⚠️ The `<p>` wrapper it used to carry is gone with the grid it
              // sat in — it is a block inside the panel's footer now, which is
              // why the panel is `w-72` rather than the `w-64` the items alone
              // would need (label `w-32` + gap + the select).
              // 📱 THE PHONE ORDERS MENU (owner 2026-10-04, Dept Orders only —
              // never the hires layout, which rebuilds its own sections): no
              // Approve/Reject (the pinned `ui/PhoneDecisionBar` has them), no
              // Edit (it leaves for the retiring full page; the row is already
              // editable), no Export CSV / Print (Share PDF instead), and the
              // corrections — Skip remaining approvals, Cancel order, Change
              // status — grouped last. Desktop unchanged.
              const phoneOrdersMenu = !hireActions && isMobileOrderViewport();
              const asCorrection = (it) => (it && phoneOrdersMenu ? { ...it, category: 'fix' } : it);
              const statusJumpNode = detail && canManageOrder(detail) ? (
                              <div data-testid={`dept-menu-status-row-${order.id}`}>
                                <InlineStatusJump
                                  order={detail}
                                  // 🔀 EXTRACTED 2026-09-11 — this body was
                                  // inline here until My Orders became a second
                                  // caller. `mergeStatusJumpResult` is it
                                  // verbatim; nothing about this page's
                                  // behaviour moved.
                                  onSaved={handleStatusJumpSaved}
                                  onCancelClick={() => runRowAction(order.id, 'cancel', order)}
                                  // 🔀 2026-09-13 — the forward-jump reason is
                                  // collected in the SHARED dialog instead of a
                                  // `window.prompt`. Omit the prop and the
                                  // component falls back to the prompt, so
                                  // `MyOrders.js`'s copy is untouched.
                                  requestOverrideReason={requestStatusJumpReason}
                                  label={phoneOrdersMenu ? 'Change status' : undefined}
                                  // 🔀 2026-10-03 — the rejection reason is
                                  // collected in the shared
                                  // `OrderReasonDialog` instead of a
                                  // `window.prompt`, so this door and the row's
                                  // own Reject ask the same question in the
                                  // same words. Omit the prop and the component
                                  // falls back to the prompt.
                                  requestRejectionReason={requestRejectionReason}
                                  testId={`dept-status-jump-${order.id}`}
                                />
                              </div>
              ) : null;

              // `rejectionReasonNode` is now built INSIDE `OrderExpandedRow`
              // (a trivial derivation from `detail.rejection_reason` — moving
              // its construction meant the read-only branch and the edit
              // branch's grid could keep reading the SAME node without the
              // page building it twice). `adminNotesNode` stays here: its
              // edit buffer is page-level state (`editingNotes`/`savingNotes`
              // keyed by order id), which Phase 1 leaves on the page — see
              // `components/OrderExpandedRow.js`'s docblock.
              const adminNotesNode = !detail ? null : canManageOrder(detail) ? (
                              // 🔻 **MOVED 2026-09-13 — it is no longer paired beside Expected
                              // Delivery, and the equal-height plumbing went with the pairing.**
                              // Owner: *"lets move it to the order details section, and update the
                              // tool tip hints about printed/not printed. and update the label as
                              // you recommended."* This node is now handed to `OrderExpandedRow`'s
                              // `adminNotesNode` prop, which routes it into `OrderForm`'s
                              // `orderDetailsRow2Rest` slot — the SECOND cell of Order Details row
                              // 2, beside Expected Delivery, under PO Number, on BOTH the
                              // read-only and edit branches.
                              // 🔻 DELETED with the pairing: this wrapper's `h-full flex flex-col`
                              // and the textarea's `flex-1 h-full`, which existed ONLY to make the
                              // box stretch to the height of the Expected Delivery cell beside it
                              // (the OLD 2026-08-19 pairing). The row-2 cell it sits in NOW is a
                              // different geometry with no equal-height requirement of its own —
                              // 🔀 CORRECTED 2026-09-13, THIRD PASS: it DOES have one again, just a
                              // different one. Owner, live in the browser: *"Internal notes should be
                              // as tall as the Expected delivery cell."* The row-2 GRID CELL already
                              // stretches to the row's height (CSS Grid's default `align-items:
                              // stretch` — `OrderForm.js`'s grid sets no `items-start` override), so
                              // this wrapper only needs to USE that stretched height:
                              // `h-full flex flex-col` on the wrapper, the label row `shrink-0` (it
                              // must not be squeezed), and the textarea's own row `flex-1 min-h-0` so
                              // it has a definite, growing height for the textarea's `h-full` to
                              // resolve against. Same idea the deleted 2026-08-19 pairing used
                              // ("wrapper's h-full flex flex-col … textarea's flex-1 h-full") for the
                              // OLD 14rem sub-grid — reused here for the new row-2 cell.
                              // `rows={2}` + `resize-y` are unchanged, and remain the MINIMUM height;
                              // `h-full` lets it grow taller to match Expected Delivery without
                              // giving up the drag-to-resize handle.
                              // ⚠️ The field, the endpoint and the gate are UNCHANGED: same
                              // `orders.admin_notes`, same `POST /api/orders/:id/notes`, same
                              // `canManageOrder` — this is a label, a tooltip and a position.
                              <div className="h-full flex flex-col">
                                <label className={`${FIELD_LABEL_CLASS} flex items-center gap-1 shrink-0`}>
                                  {INTERNAL_NOTES_LABEL}:
                                  <InfoTip title={INTERNAL_NOTES_TOOLTIP} />
                                </label>
                                <div className="flex gap-2 items-start flex-1 min-h-0">
                                  <textarea
                                    className="flex-1 min-h-0 h-full text-sm border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1.5 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-y"
                                    rows={2}
                                    placeholder={INTERNAL_NOTES_PLACEHOLDER}
                                    value={editingNotes[order.id] !== undefined ? editingNotes[order.id] : (detail.admin_notes || '')}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setEditingNotes(prev => ({ ...prev, [order.id]: e.target.value }));
                                    }}
                                    disabled={savingNotes[order.id]}
                                    data-testid={`dept-admin-notes-${order.id}`}
                                  />
                                  {editingNotes[order.id] !== undefined && editingNotes[order.id] !== (detail.admin_notes || '') && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        setSavingNotes(prev => ({ ...prev, [order.id]: true }));
                                        try {
                                          const res = await apiFetch(`${API_BASE}/api/orders/${order.id}/notes`, {
                                            method: 'POST',
                                            body: JSON.stringify({ admin_notes: editingNotes[order.id] }),
                                          });
                                          // `apiThrow` keeps the server's `error` + `code`
                                          // (this threw a fixed client string, discarding
                                          // the server's reason, until POL2 A3).
                                          if (!res.ok) await apiThrow(res);
                                          // Update local detail cache
                                          setOrderDetails(prev => ({
                                            ...prev,
                                            [order.id]: { ...prev[order.id], admin_notes: editingNotes[order.id] }
                                          }));
                                          // Update order in list too
                                          setOrders(prev => prev.map(o => o.id === order.id ? { ...o, admin_notes: editingNotes[order.id] } : o));
                                          setEditingNotes(prev => { const n = { ...prev }; delete n[order.id]; return n; });
                                          setSuccessMessage('Internal notes updated.');
                                          setTimeout(() => setSuccessMessage(''), 3000);
                                        } catch (err) {
                                          // A TOAST, through `friendlyErrorMessage` — the
                                          // page's siblings' pattern. `setError` fed the
                                          // PAGE's load banner: "Failed to load orders",
                                          // with a Try Again that refetched the list
                                          // (POL2 A3, 2026-09-25). The typed note stays.
                                          toastError(err);
                                        } finally {
                                          setSavingNotes(prev => ({ ...prev, [order.id]: false }));
                                        }
                                      }}
                                      disabled={savingNotes[order.id]}
                                      className={BTN.confirm}
                                      data-testid={`dept-admin-notes-save-${order.id}`}
                                    >
                                      {savingNotes[order.id] ? 'Saving...' : 'Save'}
                                    </button>
                                  )}
                                </div>
                              </div>
              ) : detail.admin_notes ? (
                              // The SAME read-only node My Orders and To Approve render — one
                              // component since 2026-09-13, three byte-identical copies before it.
                              <InternalNotesReadOnly notes={detail.admin_notes} />
              ) : null;

              // `readOnlyPanelBody` (PO editor / job-rig / notes / rejection
              // reason / line-items table) is now built INSIDE
              // `components/OrderExpandedRow.js`, parameterised by the props
              // assembled at `expandedContent` below. See that component's
              // docblock for what moved and what deliberately did not.

              // ════════════════════════════════════════════════════════════════
              // THE QUICK-ACTIONS ROW — ONE DEFINITION, RENDERED BY BOTH BRANCHES
              // ════════════════════════════════════════════════════════════════
              // 🔴 **HOISTED OUT OF THE READ-ONLY PANEL ON 2026-08-09, FIXING AN
              // UNREPORTED HALF OF THE DEFECT THE PRODUCT OWNER HIT ON *To
              // Approve*.** His report was about that page — *"just expanded an
              // order on my to approve page and it has no approve button?"* —
              // but this page carried the IDENTICAL shape and was measurably
              // broken too, on a fixture of one order with `can_action: true`
              // and `can_edit: true`: cards showed Approve, the table did not.
              //
              // 🕶️ **THE PREMISE THAT SHIPPED IT.** Expand-to-edit was designed
              // as ONE `? :` over the whole panel — read-only-panel **OR** edit
              // form — so everything living in the read-only branch silently
              // disappeared whenever the editor branch won. Here the editor
              // branch wins for any order that is both EDITABLE and ACTIONABLE,
              // which is the ordinary case for a buyer looking at a submitted
              // order in their own department: `canEditOrder` returns
              // `{ canEdit: true }` on `canManageDepartmentOrders`
              // (`helpers/editPermissions.js:113-116`), and `can_action` is true
              // for a buyer pending in that order's chain. Both true → Approve,
              // Reject and the whole status lifecycle vanish in the table.
              //
              // 🔑 **THE RULE THAT REPLACES IT: editing and actioning are not
              // alternatives.** Correcting an order and then approving it is one
              // workflow, and it is the owner's own justification for letting
              // approvers edit at all. Any future third panel branch must render
              // this row too.
              //
              // 🚨 **VISIBILITY IS UNCHANGED AND STILL HAS NOTHING TO DO WITH
              // `can_edit`.** Every gate inside is verbatim what it was —
              // `canApproveReject(order)` (`canActionOrder(order) && !isStoresOnly
              // && order.status === 'submitted'` — narrowed 2026-08-16, see its
              // docblock above: `can_action` alone stays true through
              // approved/ordered/partially_delivered too, which is what the
              // lifecycle items need, so Approve/Reject need the extra status
              // check the lifecycle items must NOT get) for Approve/Reject,
              // `canManageOrder(order)` for the status progression, the unscoped
              // buyer flag for Restore because its SERVER gate still is. This
              // change moves JSX; it grants nobody anything.
              //
              // ⚠️ **The action LIST is deliberately NOT shared with
              // `Approvals.js`** — this page carries the status lifecycle
              // (Mark Ordered / Upload / Print — 🔻 Partial Delivery, Mark
              // Delivered and Restore are all now DELETED: the first two are
              // redundant with the receive-item ticks 2026-08-16, Restore's
              // route was deleted 2026-08-12) and that one carries Approve +
              // Reject. What the two pages share is the PLACEMENT rule and the
              // visibility rule, not the buttons.
              //
              // 🔀 **IT IS A BUILDER TAKING ONE WRAPPER CLASS SINCE 2026-08-11,
              // AND THAT IS STILL *ONE DEFINITION* — the rule above is about the
              // ITEMS and their GATES, none of which vary.** Only the wrapper's
              // spacing and alignment do, because the group now renders in two
              // structurally different places: the read-only panel's own row at
              // the bottom (`mt-3`, inline-start, exactly as before), and — in
              // the EDIT branch — inside `OrderForm`'s `Order Details` header,
              // where a top margin would push it off the header's centre line
              // and where it must sit at the right-hand end. **Never fork the
              // items array to get a second arrangement**; that is the
              // 2026-08-09 both-branches defect.
              const buildQuickActions = (wrapperClass) => detail ? (
                          <div className={wrapperClass}>
                            {/* Mobile-only: Override at top of grid. Poke moved into the Actions menu on a phone (2026-10-01). */}
                            {(() => {
                              // Compared on approver_user_id, NEVER on
                              // approver_name — see
                              // utils/orderStepInfo.js#isApprovingSomeoneElsesStep.
                              const canOverride = stepInfo.action === 'approve' && isApprovingSomeoneElsesStep(order, currentUserId);
                              return (
                                <>
                                  {/* 🔀 Poke Approver on a PHONE is a menu
                                      item now (owner, 2026-10-01) — see the
                                      `poke` item in the Actions menu below. */}
                                  {canOverride && (
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); handleNextStepClick(order); }}
                                      className="md:hidden inline-flex items-center justify-center gap-1.5 px-3 py-1.5 min-h-[44px] bg-amber-500 text-white rounded-md hover:bg-amber-600 transition text-sm font-medium"
                                      data-testid={`dept-override-expanded-${order.id}`}
                                      title="Override the current approval step"
                                    >
                                      Override
                                    </button>
                                  )}
                                </>
                              );
                            })()}

                            {/* 🔻 **`View Details` AND `Edit` LEFT THIS ROW ON
                                2026-08-10 and became menu items** — the owner
                                named both in the list the single actions menu
                                replaces. Their gates, their handlers and their
                                testids went with them unchanged; see
                                `deptMenuItems` below, where each gate is written
                                beside its own item exactly as it was written
                                beside its own button here. 🔻 `View Details`
                                is now ITSELF DELETED from the plain Department
                                Orders menu (2026-10-04, owner ruling) — kept
                                only for Department Hires, gated on
                                `hireActions`; `Edit` is unaffected.
                                🚨 **Approve and Reject did NOT leave.** He was
                                offered "everything into the menu" and chose to
                                keep these two visible AND put them in the menu:
                                the missing Approve button was a defect he
                                reported on 2026-08-09, and Department Orders
                                shows orders he actions all day. Two triggers,
                                ONE handler, DISTINCT testids — see the guard in
                                `DepartmentOrders.actionsMenu.test.js`. */}

                            {/* Approve/Reject buttons for actionable orders.
                                Hidden for Stores-only viewers — they never approve.
                                Submitted-only — see `canApproveReject` above. */}
                            {canApproveReject(order) && (
                              // 📱 Pinned to the bottom on a phone, away from Actions ▾,
                              // and stepped aside while the form has unsaved changes
                              // (owner 2026-10-04) — `ui/PhoneDecisionBar`. Desktop unchanged.
                              <PhoneDecisionBar testId={`dept-decision-bar-${order.id}`}>
                                {/* `disabled={actionBusy}` — the submit-guard's
                                    render mirror. The REF inside `guard` is
                                    what stops the fast double-click; this is
                                    the visible half, and it must sit on BOTH
                                    copies of Approve/Reject (these buttons AND
                                    the menu items below) — the owner ruled two
                                    surfaces, so both disable together. */}
                                <button
                                  type="button"
                                  disabled={actionBusy}
                                  onClick={(e) => { e.stopPropagation(); requestApprove(order); }}
                                  className="inline-flex items-center justify-center md:justify-start gap-1.5 px-3 py-1.5 min-h-[44px] md:min-h-0 bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50 rounded-md transition text-sm font-medium border border-green-200 dark:border-green-800 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid={`dept-approve-${order.id}`}
                                  title="Approve this order and advance it to the next approval step"
                                >
                                  Approve
                                </button>
                                <button
                                  type="button"
                                  disabled={actionBusy}
                                  onClick={(e) => { e.stopPropagation(); runRowAction(order.id, 'reject', order); }}
                                  className="max-md:order-first inline-flex items-center justify-center md:justify-start gap-1.5 px-3 py-1.5 min-h-[44px] md:min-h-0 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 rounded-md transition text-sm font-medium border border-red-200 dark:border-red-800 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid={`dept-reject-${order.id}`}
                                  title="Send back to the submitter for correction — they can edit and resubmit"
                                >
                                  Reject
                                </button>
                              </PhoneDecisionBar>
                            )}

                            {/* ════════════════════════════════════════════════
                                THE ACTIONS MENU
                                ════════════════════════════════════════════════
                                🚨 **EVERY GATE BELOW IS VERBATIM WHAT IT WAS ON
                                THE BUTTON IT REPLACES, WRITTEN BESIDE ITS OWN
                                ITEM.** They are moved, not rewritten: not merged
                                into one condition, not hoisted to page level,
                                and emphatically not re-derived from a page-level
                                `isAdmin`/`isBuyer`. Moving JSX must grant nobody
                                anything.
                                🔑 `OrderActionsMenu` resolves NOTHING — it takes
                                this finished array and renders it, the same
                                contract `OrderTableView` has and for the same
                                reason.
                                🚨 **EVERY MUTATING ITEM GOES THROUGH
                                `runRowAction`, WHICH IS THE SAVE-THEN-ACT PATH.**
                                It routes `view`/`edit` (NAVIGATING_ACTIONS)
                                through `continueWhenClean()` and everything else
                                through `saveThenRun`, so a dirty form is saved
                                first and a FAILED save can never act. That is
                                structural, not guarded — do not add a second
                                call path from inside the menu. */}
                            <OrderActionsMenu
                              triggerTestId={`dept-actions-menu-${order.id}`}
                              panelTestId={`dept-actions-panel-${order.id}`}
                              ariaLabel={`Actions for order ${getOrderNumber(order)}`}
                              idPrefix={`dept-actions-${order.id}`}
                              // ══════════════════════════════════════════════
                              // HEADED SECTIONS (2026-08-11; MERGED 2026-09-13)
                              // ══════════════════════════════════════════════
                              // The owner's complaint was that the override
                              // *"doesn't fit"* — space and organisation. These
                              // headings answer that, in ONE scrolling
                              // panel: every item stays in the DOM and every
                              // action stays ONE click. A category RAIL was
                              // built and rejected; see `OrderActionsMenu`'s
                              // docblock for why, and do not reintroduce it.
                              // 🚨 **CATEGORIES ARE PRESENTATION. A `category`
                              // key can neither show nor hide anything** — every
                              // item below keeps the verbatim gate it already
                              // had, and an empty category renders no heading.
                              // **Never express a gate as a category.**
                              // 🔀 **TWO headings, not three — Pete, 2026-09-13,
                              // asked "Thoughts on changing the action menu sub
                              // heading 'Status' to 'Next Action' and putting it
                              // top in the list?", the controller recommended
                              // MERGING `Approval` and `Status` into ONE group,
                              // and Pete approved ("sounds great please
                              // proceed").** `Approval` and `Status` are now ONE
                              // group, `Next step`, placed FIRST — every item
                              // that was `category: 'approval'` or
                              // `category: 'status'` is now `category: 'next'`
                              // (the descriptor table in
                              // `constants/orderLifecycleMenuItems.js` carries
                              // the rename for approve/reject/approveOnBehalf/
                              // ordered, so `MyOrders.js`'s flat menu — which
                              // ignores `category` entirely — is unaffected).
                              // `Order` (Edit, export, PDF, print, share… —
                              // plus View Details, DELETED here 2026-10-04,
                              // owner ruling; kept only for Department Hires)
                              // is now SECOND. The status
                              // override is `Next step`'s FOOTER — the slot it
                              // occupied as `Status`'s footer before the merge.
                              // 🔻 **CORRECTED 2026-09-13 — THIS SAID THE
                              // APPROVAL OVERRIDE WAS "Deliberately NOT added
                              // here". IT IS ADDED HERE NOW**, as the `next`
                              // item `Skip remaining approvals…`, by owner
                              // ruling (*"the override should require a
                              // reason"* — yes to the menu item, and the full
                              // order page `/orders/:id` is to be retired
                              // afterwards). It superseded the 2026-08-10
                              // *"keep the override button on the full order
                              // page for now"*.
                              // 🪤 **AND THE OLD PARAGRAPH WAS ABOUT A
                              // DIFFERENT CONTROL, WHICH IS WHY IT READ AS
                              // SETTLED.** The `Override` BUTTON it described
                              // (`override-button-<id>` in the collapsed card, since retired,
                              // `dept-override-expanded-<id>` in the mobile
                              // quick-actions group) is APPROVE-ON-BEHALF — it
                              // calls `handleNextStepClick` → `requestApprove`
                              // and never touches
                              // `POST /api/approvals/orders/:id/override`. Both
                              // are UNCHANGED by this addition. Two different
                              // things wearing one word is exactly why the new
                              // item is labelled by what it DOES.
                              // 🔀 2026-09-28 — on the HIRES pages only,
                              // `withHireMenu` re-orders these sections and
                              // adds the Hire items (`components/hires/
                              // hireOrderMenu.js`); with no `hireActions` it
                              // returns this object unchanged, so Department
                              // Orders renders exactly this menu.
                              {...withHireMenu(hireActions, order, { dirty: editDirtyId === order.id }, {
                              categories: phoneOrdersMenu ? [
                                { key: 'next', label: 'Next step' },
                                { key: 'order', label: 'Order' },
                                { key: 'fix', label: 'Corrections', footer: statusJumpNode },
                              ] : [
                                {
                                  key: 'next',
                                  label: 'Next step',
                                  // The status override is a CONTROL you set,
                                  // not an action you fire, so it keeps its
                                  // separator — as the footer of the section
                                  // it belongs to rather than of the whole
                                  // menu.
                                  footer: statusJumpNode,
                                },
                                { key: 'order', label: 'Order' },
                              ],
                              items: [
                                // 📱 POKE APPROVER — PHONE ONLY (owner,
                                // 2026-10-01). Was a separate `md:hidden`
                                // button beside this menu: same gate
                                // (`canPokeOrder`), same handler, same testid.
                                // `isMobileOrderViewport()` keeps it off the
                                // desktop menu, which is unchanged.
                                // SUBMITTED ONLY here (owner, 2026-10-11: "for now we can just leave it to the individual who placed the order to poke") - the approved-stage "Poke buyer" is My Orders' alone.
                                // 4A (owner 2026-10-29): offered only to whom the SERVER admits — the
                                // requester (by id) or `canManageDepartmentOrders` in the ORDER'S OWN
                                // department (a site admin holds it) — never a plain viewer.
                                order.status === 'submitted' && canPokeOrder(order, currentUserId) && isMobileOrderViewport()
                                  && (String(order.user_id) === String(currentUserId) || isAdmin || capabilitiesFor(order.department_id).canManageDepartmentOrders === true) ? {
                                  key: 'poke', category: 'order',
                                  label: pokeLoading[order.id] ? 'Sending...' : pokeLabel(order),
                                  tone: 'nav',
                                  testId: `dept-poke-expanded-${order.id}`,
                                  title: pokeTitle(order),
                                  disabled: !!pokeLoading[order.id],
                                  onClick: (e) => handlePoke(order.id, e),
                                } : null,
                                // 🔻 `View Details` REMOVED FROM DEPARTMENT
                                // ORDERS (owner, 2026-10-04: "seems like the
                                // expanded order gives us the info") —
                                // removed from all three order list Actions
                                // menus in the same change. KEPT for HIRES
                                // ONLY: Department Hires runs this same hook
                                // with `hireActions` set, and
                                // `hireOrderMenu.js#arrangeHireOrderMenu`
                                // relabels this exact item to "View in
                                // orders" — the hires page's one route to
                                // /orders/:id (its PO/Order cell no longer
                                // links there). Pre-deletion SHA 204a6b95.
                                hireActions ? {
                                  key: 'view', category: 'order',
                                  label: 'View Details',
                                  tone: 'nav',
                                  testId: `dept-menu-view-full-${order.id}`,
                                  title: 'Open the full order detail page',
                                  onClick: () => runRowAction(order.id, 'view', order),
                                } : null,
                                // 🔻 `Edit` DELETED 2026-10-09 (owner, demo run-through: "remove"). It
                                // navigated to the old `/orders/:id/edit` page; the expanded row IS the
                                // editable order wherever `can_edit` is true. Pre-deletion SHA b9686c07.
                                // Approve / Reject — the SECOND trigger for each.
                                // Same gate as the visible buttons above, same
                                // `runRowAction` call, DIFFERENT testid. The owner
                                // asked for both surfaces; two components stamping
                                // one testid is a documented defect class here, so
                                // the pair is guarded by a
                                // `querySelectorAll(...).length === 1` assertion.
                                // `disabled: actionBusy` on every MUTATING item
                                // below — the second copy of the submit-guard's
                                // render mirror (the inline buttons carry the
                                // first; both surfaces must disable together).
                                // 🔀 2026-09-11: the DESCRIPTOR (key/category/
                                // label/tone/testid slug/title) moved to
                                // `constants/orderLifecycleMenuItems.js` so
                                // `MyOrders.js` offers the identical item in My
                                // Space rather than a copy that can drift. THE
                                // GATE AND THE HANDLER DID NOT MOVE and must
                                // not — they are still written verbatim beside
                                // the item, per this page's own rule in
                                // `client/src/pages/CLAUDE.md`. Rendered output
                                // is byte-identical; the two testids are
                                // unchanged.
                                canApproveReject(order) && !phoneOrdersMenu && orderLifecycleMenuItem('approve', {
                                  testIdPrefix: 'dept',
                                  orderId: order.id,
                                  disabled: actionBusy,
                                  onClick: () => requestApprove(order),
                                }),
                                canApproveReject(order) && !phoneOrdersMenu && orderLifecycleMenuItem('reject', {
                                  testIdPrefix: 'dept',
                                  orderId: order.id,
                                  disabled: actionBusy,
                                  onClick: () => runRowAction(order.id, 'reject', order),
                                }),
                                // 🔀 APPROVE ON BEHALF (2026-09-11, owner ruling A) — THE
                                //    MANAGER'S FAST PATH, NOW LABELLED. Until today
                                //    `GET /api/orders/department` forced `can_action`
                                //    true on EVERY submitted order in the department, so
                                //    the plain `Approve` above appeared on other people's
                                //    steps and said nothing about it. That override is
                                //    deleted server-side, so `canApproveReject` —
                                //    `can_action && !isStoresOnly && submitted` — now
                                //    yields Approve/Reject ONLY on the viewer's own step.
                                //    VERIFIED by reading, not assumed: no second predicate
                                //    was added to that gate, and none is needed.
                                //
                                // 🔑 THE CAPABILITY DID NOT MOVE, ONLY THE LABEL. A
                                //    manager has always been able to approve a colleague's
                                //    step — `POST /api/approvals/orders/:id/approve` admits
                                //    them on `canManageDepartmentOrders` and is UNTOUCHED
                                //    by this change. Deleting the flag would have taken
                                //    that fast path away as a side effect of fixing a
                                //    COUNT, so it comes back here as its own item, saying
                                //    out loud what it does. Same `category` as Approve: it
                                //    is the same verb.
                                //
                                // 🚨 THE GATE IS `isApprovingSomeoneElsesStep`, THE SHARED
                                //    PREDICATE, NOT A HAND-WRITTEN CONJUNCTION. It compares
                                //    `approval_info.approver_user_id` against the viewer's
                                //    id and NEVER the display name (names are not unique in
                                //    this app — the name-as-identity class this repo has
                                //    shipped three fixes for), and a NULL
                                //    `approver_user_id` returns FALSE: *an ambiguous
                                //    template name binds NOBODY*, so there is no real
                                //    person to name and nothing true the label could say.
                                //    It is the SAME predicate `requestApprove` uses to
                                //    decide whether to raise the dialog, which is what
                                //    makes 'this item always opens the dialog' structural
                                //    rather than a coincidence two call sites must
                                //    maintain. It also carries the `status === 'submitted'`
                                //    and `approval_info` guards, so they are not repeated.
                                //
                                // 🔴 NO `rejectOnBehalf` TWIN, and that is a decision. It
                                //    was not requested; a rejection sends the order back to
                                //    its requester with a reason attributed to whoever
                                //    rejected it, and inventing an on-behalf form of that
                                //    is a product call nobody has made. Reject stays
                                //    own-step only.
                                //
                                // 🪤 `isStoresOnly` is NOT repeated here: a stores-only
                                //    viewer is `currentRole === 'user'` and holds no
                                //    `canManageDepartmentOrders`, so `canManageOrder` is
                                //    already false for them. The plain Approve above needs
                                //    the term because it keys on `can_action`, which Stores
                                //    DOES get — on the fulfilment statuses.
                                canManageOrder(order)
                                  && isApprovingSomeoneElsesStep(order, currentUserId)
                                  && orderLifecycleMenuItem('approveOnBehalf', {
                                    testIdPrefix: 'dept',
                                    orderId: order.id,
                                    disabled: actionBusy,
                                    labelArgs: { approverName: order.approval_info?.approver_name },
                                    // 🔑 ONE FUNNEL. `requestApprove` is the only place
                                    // `approveOnBehalfPrompt` is set, and it raises the
                                    // dialog on exactly this predicate — so this item
                                    // CANNOT act without confirmation, rather than being
                                    // trusted not to. Confirm then replays the identical
                                    // `runRowAction(id, 'approve', order)` the inline
                                    // button would have made, through save-then-act like
                                    // every other lifecycle item.
                                    onClick: () => requestApprove(order),
                                  }),
                                // Order-manager status progression. 🔻 **THIS SAID "All
                                // three PATCH /api/orders/:id/status" until 2026-08-16 —
                                // `Mark Ordered` is the LAST ONE LEFT** (Partial Delivery
                                // and Mark Delivered deleted below, redundant with the
                                // receive-item ticks). PATCH /api/orders/:id/status via
                                // handleAction, which the server gates with
                                // canManageDepartmentOrders in the order's own department
                                // — so this gate asks exactly that. (Restore used to be
                                // excluded from this group because it called
                                // PATCH /api/orders/:id/uncancel, gated on the unscoped
                                // `req.isBuyer`. That route was DELETED 2026-08-12 and
                                // the exclusion is moot: there is no Restore control
                                // anywhere in the client, and leaving `cancelled` is this
                                // group's own PATCH /:id/status work.)
                                // 🚨 SKIP REMAINING APPROVALS (2026-09-13 owner
                                //    ruling) — the approval OVERRIDE, which
                                //    until today had no home outside the full
                                //    order page. POSITION IS DELIBERATE: after
                                //    `Approve on behalf of …` and before
                                //    `Mark Ordered`, because it is an
                                //    APPROVAL-STAGE act — it ends the chain, it
                                //    does not start the next stage.
                                // 🔑 THE GATE IS `canSkipApprovals`, THE SHARED
                                //    PREDICATE defined beside `canApproveReject`
                                //    — it mirrors the server route term for
                                //    term, on `canManageDepartmentSettings` in
                                //    THE ORDER'S OWN department, and NOT
                                //    `canManageOrder` (a different column; see
                                //    the predicate's own docblock for why the
                                //    route chose settings over orders).
                                // 🪤 `saveThenRun` FIRST, dialog SECOND, and
                                //    that ordering is precedented rather than
                                //    chosen: `handleAction`'s `reject` and
                                //    `cancel` branches both prompt AFTER the
                                //    save, and a `null` there aborts the action
                                //    with the save already done. Asking for a
                                //    justification before asking whether to save
                                //    the edits would put the two questions in
                                //    the wrong order for the user too.
                                asCorrection(canSkipApprovals(order) && orderLifecycleMenuItem('override', {
                                  testIdPrefix: 'dept',
                                  orderId: order.id,
                                  disabled: actionBusy,
                                  onClick: () => saveThenRun(order.id, 'override', () => requestSkipApprovals(order, {
                                    onResolve: (reason) => { if (reason) performOverride(order, reason); },
                                  })),
                                })),
                                canManageOrder(order) && order.status === 'approved' && orderLifecycleMenuItem('ordered', {
                                  testIdPrefix: 'dept',
                                  orderId: order.id,
                                  disabled: actionBusy,
                                  onClick: () => runRowAction(order.id, 'ordered', order),
                                }),
                                // 🔻 **`Partial Delivery` AND `Mark Delivered` ITEMS ARE
                                // DELETED (2026-08-16) — owner: *"remove the discussed
                                // items from the action menu"* (Partial Delivery + Mark
                                // Delivered). Both were a MANUAL status set via
                                // `PATCH /:id/status`, redundant with the receive-item
                                // endpoints that already recompute this exact status from
                                // the line-item ticks — see the deleted `handleAction`
                                // branches above. Select-all in the "Line Items —
                                // received (x/N)" header is now the one-click "whole
                                // order delivered"; the per-item ticks are the one-click
                                // "some items delivered". `Mark Ordered` above is
                                // UNCHANGED — nothing derives it from ticks, so it stays
                                // the only route onto `ordered`.
                                // 🔀 2026-09-13 (Pete): "Mark all Delivered" is BACK as a
                                // Next-step item — see the `receive-all` item directly
                                // below. Pete reversed the 2026-08-16 deletion, but this
                                // paragraph's "ticking items is now the only route to
                                // either status" is STILL TRUE: the new item is a
                                // one-click SHORTCUT onto the SAME receive-all endpoint
                                // the Select-all checkbox already calls (`receivedHandlers
                                // .receiveAll`), never a revival of the deleted manual
                                // `PATCH /:id/status` branches above. One fact, two
                                // surfaces.
                                showReceivedTicks && !allItemsReceived && someAwaitingDelivery && {
                                  key: 'receive-all', category: 'next',
                                  label: 'Mark all Delivered',
                                  tone: 'deliver',
                                  // Same predicates the tick cluster itself already
                                  // resolves at page level (canReceiveItems /
                                  // canReceiveNow / showReceivedTicks, above) —
                                  // never re-derived here. Shown only while some line
                                  // is ordered and undelivered (`someAwaitingDelivery`,
                                  // 2026-10-28); otherwise the same `actionBusy ||
                                  // receivingThisOrder` every mutating item and every
                                  // tick already use.
                                  disabled: !canReceiveNow || actionBusy || receivingThisOrder,
                                  title: !canReceiveNow
                                    ? RECEIVING_LOCKED_TOOLTIP
                                    : 'Tick every line item on this order as delivered',
                                  testId: `dept-menu-receive-all-${order.id}`,
                                  // 🚨 THE MECHANISM IS THE EXISTING TICK CLUSTER'S
                                  // `receivedHandlers.receiveAll`, NOT `handleAction`/
                                  // `runRowAction` and NOT the status override — so
                                  // ticks, derived status, audit and refresh are
                                  // IDENTICAL to ticking the header checkbox. Routed
                                  // through `saveThenRun` directly (never
                                  // `runRowAction`, which always calls `handleAction`)
                                  // because the save-then-act GUARANTEE still applies
                                  // — a dirty inline edit must be saved (or the user
                                  // asked) before this fires — but the ACTION itself
                                  // is a different endpoint. `'receive_all'` (not
                                  // `'receive-all'`) is the `kind` so it is picked up
                                  // by `saveThenActPrompt.test.js`'s source scan and
                                  // gets its own copy rather than the generic
                                  // fallback — see `constants/saveThenActPrompt.js`.
                                  onClick: () => saveThenRun(order.id, 'receive_all', () => receivedHandlers.receiveAll(order.id, true)),
                                },
                                // Order Completion (2026-08-16) — a completion-flag
                                // OVERLAY, never a status; `status` itself is
                                // unchanged by either action. Gated exactly like
                                // the server: canManageOrder + status ∈
                                // {delivered, partially_delivered} + not already
                                // complete. The click does NOT call `runRowAction`
                                // directly — it opens the ALWAYS-SHOWN confirm
                                // dialog (`completionPrompt`), whose Continue is
                                // what fires `runRowAction` (still through
                                // save-then-act, same as every other lifecycle
                                // item here). See docs/superpowers/specs/
                                // 2026-08-16-order-completion.md.
                                canManageOrder(order)
                                  && ['delivered', 'partially_delivered'].includes(order.status)
                                  && !isOrderComplete(order)
                                  && !orderHasHireStillOut(detail && detail.items) && {
                                  key: 'complete', category: 'next',
                                  label: 'Mark as Complete',
                                  tone: 'deliver',
                                  disabled: actionBusy,
                                  testId: `dept-menu-complete-${order.id}`,
                                  title: "Mark this order as finished. It keeps its current status and gets a ✓ Complete badge — untick 'Completed' in Filters to hide finished orders.",
                                  // 🪤 `has_delivery_paperwork` is a LIST-row
                                  // field (all three list endpoints), NOT sent
                                  // by `GET /api/orders/:id` — read it off
                                  // `order`, never off `detail`, or every
                                  // dialog reads "no paperwork attached" on an
                                  // order that plainly has some.
                                  onClick: () => setCompletionPrompt({
                                    order,
                                    items: detail ? detail.items : undefined,
                                    hasDeliveryPaperwork: order.has_delivery_paperwork,
                                    mode: 'complete',
                                  }),
                                },
                                canManageOrder(order) && isOrderComplete(order) && {
                                  key: 'reopen', category: 'next',
                                  label: 'Re-open',
                                  tone: 'progress',
                                  disabled: actionBusy,
                                  testId: `dept-menu-reopen-${order.id}`,
                                  title: 'Show this order as active again — removes the ✓ Complete badge. Its status is unchanged.',
                                  onClick: () => setCompletionPrompt({ order, mode: 'reopen' }),
                                },
                                // 🔀 2026-10-03 (owner): "action menu should include cancel
                                // order for orders that havent been approved yet" — a
                                // SUBMITTED order gets Cancel here, gated as the server's
                                // cancel route is (canManageOrder), through the same
                                // runRowAction → reason dialog flow. Beyond submitted the
                                // status override below stays the route.
                                asCorrection(canManageOrder(order) && order.status === 'submitted' && {
                                  key: 'cancel', category: 'next',
                                  label: 'Cancel order',
                                  tone: 'reject',
                                  disabled: actionBusy,
                                  testId: `dept-menu-cancel-${order.id}`,
                                  title: 'Cancel this order — it has not been approved yet',
                                  onClick: () => runRowAction(order.id, 'cancel', order),
                                }),
                                // (History) Cancel was deliberately NOT an item — the status
                                // override in the footer was the only route to it
                                // (the standalone Cancel button was removed
                                // 2026-05-28 in favour of that dropdown's
                                // `cancelled` option, which delegates to the same
                                // handleAction(id, 'cancel', …) flow).
                                //
                                // 🔻 **`Restore` IS DELETED (2026-08-12) — LEAVING
                                // `cancelled` IS THE STATUS OVERRIDE'S JOB NOW, AND
                                // THIS IS A GATE CONSOLIDATION AS WELL AS A UI ONE.**
                                // Product owner: *"the restore is not obvious to what
                                // it does"*, and *"as long as buyers/supervisors can
                                // put the order to any key stage and the approval
                                // flows etc run as normal then we are good"*.
                                //
                                // 🔑 **NOBODY GAINED THE ABILITY TO UN-CANCEL — the
                                // override ALREADY moved an order out of `cancelled`
                                // before this change**; it simply failed to clear the
                                // cancellation reason and the before-cancel marker,
                                // which `PATCH /:id/status` now does. So this made an
                                // existing capability correct rather than conferring a
                                // new one.
                                // 🔴 **Two callers lost the button and only one was
                                // real.** The server's owner branch
                                // (`order.user_id === req.userId`) was never reachable
                                // here — this gate required buyer/`isBuyer`, so no
                                // owner ever saw it. The genuine loss is an UNSCOPED
                                // buyer who is a member of the order's department but
                                // does not hold `canManageDepartmentOrders` there —
                                // the legacy flag that is being deleted.
                                // 🟢 **THE SEQUEL, 2026-08-12: `PATCH /api/orders/:id/uncancel`
                                // IS NOW DELETED, and so are the three client controls
                                // that called it** — `pages/MyOrders.js`'s Restore Order
                                // button and `pages/OrderPage.js`'s mobile AND desktop
                                // copies. `online-lifecycle-restore-ui.spec.js` is
                                // deleted; four other specs were edited.
                                // ⚠️ **The paragraph this replaces was RIGHT and its
                                // caution still governs: deleting a BUTTON mitigates
                                // the resurrection defect not at all.** What the route
                                // did was flip `order_approvals.overall_status`
                                // `rejected` → `pending` while restoring a `rejected`
                                // status, so the order re-entered the buyer's To
                                // Approve queue with no resubmission. Deleting this
                                // button closed the PRIVILEGED door; deleting MyOrders'
                                // closed the UNPRIVILEGED one, which is the half that
                                // made it reproducible by a plain crew member in two
                                // requests.
                                // 🔑 **NEITHER DELETION CLOSED THE SHAPE.**
                                // `POST /api/backup/department/restore` re-inserts
                                // `order_approvals` verbatim from a caller-supplied
                                // archive, so the inconsistent pair is still writable.
                                // What closes it is the `'rejected'` exclusion added to
                                // BOTH To Approve queries in `routes/approvals.js` on
                                // the same branch — pinned by
                                // `server/tests/regression/approvals-terminal-status-guard.test.js`.
                                // Enumerate the WRITERS, not the readers.
                                //
                                // 🚨 Do NOT re-add a `Restore` item — this now means
                                // "there is no route to call". If the control ever
                                // needs to come back it is a `cancelled`-only shortcut
                                // that PATCHes `/status`, never `/uncancel`.
                                // Export CSV — THIS ORDER and its line items, not
                                // the filtered list (the owner was offered the list
                                // export and chose this). Ungated, and that is a
                                // measurement rather than a preference: the server
                                // route post-filters every row through `canViewOrder`
                                // and emits no accounting column at all. The full
                                // check is at `utils/exportSingleOrder.js`.
                                !phoneOrdersMenu && {
                                  key: 'export-csv', category: 'order',
                                  label: CSV_MENU_LABEL,
                                  testId: `dept-menu-export-csv-${order.id}`,
                                  title: `Download this order and its line items as ${CSV_LABEL}`,
                                  onClick: async () => {
                                    try {
                                      await exportSingleOrderCsv(detail || order);
                                    } catch (err) {
                                      // A toast via `friendlyErrorMessage`, not the
                                      // page's "Failed to load orders" banner (owner
                                      // ruling 2026-09-25, same fix as Internal notes).
                                      toastError(err);
                                    }
                                  },
                                },
                                // Download PDF / Print… / Share… — `OrderPdfButton`'s
                                // three actions, flattened. A COMPONENT rather than
                                // three items because they share a `pendingAction`
                                // and this array is built inside `orders.map(…)`,
                                // where a hook cannot be called.
                                {
                                  key: 'pdf', category: 'order',
                                  render: (close) => (
                                    <OrderPdfMenuItems
                                      order={detail}
                                      onDone={close}
                                      testIdPrefix={`dept-${order.id}`}
                                      hidePrintWhenShareable={phoneOrdersMenu}
                                    />
                                  ),
                                },
                              ],
                              })}
                            />
                            {/* 🔻 "Upload Paperwork" WAS HERE AND IS DELETED
                                (2026-08-10). It did not open an uploader — it
                                NAVIGATED to `/orders/:id?upload=1`, i.e. it left
                                the page, discarding the expanded row and any
                                in-progress inline edit, to reach a section that
                                now renders in this very panel. That navigation
                                is the step the product owner asked to remove.
                                Delivery paperwork is now a disclosure inside
                                `components/OrderExpandedDetails.js`, carrying
                                the SAME gate this button had — which is why the
                                condition was lifted verbatim into
                                `canUploadDelivery` rather than rewritten.
                                ⚠️ The trade is one click for discoverability:
                                the inline section is collapsed by default, so
                                uploading is now expand-row → expand-section
                                rather than one click plus a page load. If the
                                owner wants the one-click affordance back, the
                                cheap fix is a signal prop that opens the
                                delivery disclosure — NOT restoring a navigating
                                button. `runRowAction(id, 'upload')` and its
                                `NAVIGATING_ACTIONS` entry are kept: `?upload=1`
                                still works as a deep link.
                                ⚠️ **"and `MyOrders.js`, which has no table
                                layout and no inline panel, still relies on
                                that route" stood here and is now FALSE ON
                                BOTH COUNTS, predating this branch** — MyOrders
                                took a table layout on 2026-08-11 and, since
                                the Phase 2 My Orders/Dept Orders convergence,
                                renders the SAME `OrderExpandedRow` (with the
                                same `OrderExpandedDetails` delivery
                                disclosure this comment describes) inline.
                                Its own `'upload'` branch in
                                `handleQuickAction` is unreached by anything in
                                that file today — a menu item nowhere calls
                                `runRowAction(id, 'upload')` there — so if the
                                deep link is still exercised it is via
                                someone navigating straight to
                                `/orders/:id?upload=1` (e.g. a notification
                                link) rather than because MyOrders lacks an
                                inline alternative. Not re-investigated further
                                here — this file's own comment was simply
                                wrong about MyOrders' capabilities, and that is
                                the part corrected. */}

                            {/* Duplicate Order button - disabled for now, may re-enable later
                            {(currentRole === 'buyer' || isBuyer || isAdmin) && (
                              <button
                                onClick={(e) => { e.stopPropagation(); runRowAction(order.id, 'duplicate', order); }}
                                className="inline-flex items-center justify-center md:justify-start gap-1.5 px-3 py-1.5 min-h-[44px] md:min-h-0 bg-slate-50 text-slate-700 hover:bg-slate-100 dark:bg-slate-900/30 dark:text-slate-300 dark:hover:bg-slate-900/50 rounded-md transition text-sm font-medium"
                                data-testid={`dept-duplicate-${order.id}`}
                                title="Create a new order pre-filled with this order's details"
                              >
                                Duplicate
                              </button>
                            )}
                            */}

                            {/* 🔻 `<OrderPdfButton order={detail} />` WAS HERE AND
                                IS GONE (2026-08-10). Its "Download / Print"
                                dropdown is one of the controls the owner asked
                                the single actions menu to replace, and its three
                                actions are now rows inside that menu
                                (`OrderPdfMenuItems`, above). 🪤 It could NOT
                                simply be dropped into the panel as-is: a
                                dropdown inside a dropdown is clipped by
                                `ui/Dropdown`'s `overflow-y-auto` and its
                                document-level `mousedown` close listener fights
                                `ui/Dropdown`'s — both measured, both recorded at
                                `hooks/useSharePdf.js`. The component itself is
                                untouched and still serves `MyOrders.js` and
                                `OrderPage.js`. */}
                          </div>
              ) : null;

              // 🔀 **THE READ-ONLY PANEL'S ARRANGEMENT MOVED (2026-08-16) —
              // SUPERSEDES THE "STAYS AT THE BOTTOM" RULING RECORDED HERE
              // UNTIL NOW.** Owner: *"I asked before for the departments
              // orders and my orders to look as similar as possible and share
              // code where possible"*, plus *"which means the actions menu
              // will end up in the Order detail section header on the right
              // hand side"*. `quickActionsRow` now renders INSIDE the shared
              // "Order Details" header `OrderExpandedRow`'s read-only branch
              // builds (`components/OrderSectionHeader.js`), right-aligned on
              // `md+`, same as the edit branch's `quickActionsHeaderGroup`
              // below — which is why this wrapper class now matches that one
              // (no `mt-3`; the header bar owns its own spacing).
              const quickActionsRow = buildQuickActions('grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center');

              // The EDIT branch's arrangement. The ONLY difference is the
              // missing `mt-3`: the header bar owns its own `py-1`, and a top
              // margin there would push the group off the title's centre line.
              // **Right-alignment is NOT set here** — `OrderForm`'s header wraps
              // this in `w-full md:w-auto md:ml-auto`, so a `md:justify-end`
              // beside it would be an inert second answer to one question.
              // Below `md` it is byte-identical to the read-only arrangement —
              // same 2-column grid, same `min-h-[44px]` tap targets — because
              // the header bar wraps and hands the group a full-width line.
              const quickActionsHeaderGroup = buildQuickActions('grid grid-cols-2 gap-2 md:flex md:flex-wrap md:items-center');

              // ── THE THREE SECTIONS THAT CLOSED THE GAP WITH `/orders/:id` ──
              // (2026-08-10) Attachments + delivery paperwork, the read-only
              // approval stepper, and activity history. Product owner:
              // *"expanding an order still doesnt show full detials including
              // history?"*
              //
              // 🔑 RENDERED BY **BOTH** PANEL BRANCHES, unlike the PO number /
              // job / notes / line items, and the reason is the one the
              // sanctioned-exception box gives for the three controls above:
              // `OrderForm` has NO counterpart for any of these, so there is
              // nothing for the form to supersede and no way for two copies to
              // disagree. The node is built once here and rendered in each.
              //
              // 🚨 EVERY GATE IS RESOLVED HERE, BESIDE THE PAGE'S OTHERS, and
              // each mirrors the SERVER gate for the SAME action — which for
              // two of these is deliberately NOT `canManageDepartmentOrders`.
              // See `components/OrderExpandedDetails.js`'s docblock, which
              // quotes each route line.
              //
              // 📦 `canUploadDelivery` and `canReceiveItems` now express the
              // SAME server predicate. Tranche 3 increment 3d (2026-08-14)
              // migrated `POST /api/orders/:id/files?category=delivery` off the
              // any-department `req.isBuyer` onto canManageDepartmentOrders in
              // the ORDER'S OWN department, so it is now identical to
              // `PATCH /api/orders/:id/items/:itemId/received`:
              // `isGlobalAdmin || canManageDepartmentOrders(order's OWN dept) ||
              // isOwner || isStores`. Resolved ONCE here, beside the page's other
              // gates. 🔑 `isAdmin` is the RAW context site-admin flag (not the
              // buyer-inclusive `currentRole`); the dept-manager term is
              // `canManageOrder(detail)` resolved in the order's own department
              // (NOT the unscoped `req.isBuyer`, so no new manifest read); a
              // buyer who raised their own order is admitted by the owner term.
              // ⚠️ This REPLACES the pre-3d delivery gate
              // `currentRole === 'buyer' || isAdmin || canManageStores`, which
              // under-served a correctly-created department buyer and a
              // supervisor (both now allowed server-side). 🟢 A
              // `sub_department_lead` satisfies neither term, so nothing leaks.
              // `isOrderOwner` / `canReceiveItems` are resolved at the TOP of
              // this map callback since 2026-08-15 — the line-item ticks (both
              // panel branches) read them before this point.
              const canUploadDelivery = canReceiveItems;

              // Per FILE, because `DELETE /api/orders/:id/files/:fileId` decides
              // per file (`routes/orders.js`): moderators (admin / an
              // order-manager in the ORDER'S OWN department) may remove any; the
              // UPLOADER may remove their own; a legacy row with a NULL uploader
              // falls back to `canEditOrder`. 🔑 The moderator term is
              // `canManageOrder(detail)` — canManageDepartmentOrders resolved in
              // the order's own department — mirroring the server gate MIGRATED
              // off the any-department `req.isBuyer` in tranche 3 increment 3d.
              // 🪤 `currentUserId` is a STRING from localStorage and
              // `uploaded_by_user_id` is a NUMBER, so the comparison is coerced —
              // a bare `===` is always false and the uploader branch never fires.
              const canRemoveOrderFile = (file) => {
                if (!file) return false;
                if (isAdmin || canManageOrder(detail)) return true;
                const uploader = file.uploaded_by_user_id;
                if (uploader != null && currentUserId !== '' && String(uploader) === String(currentUserId)) return true;
                return uploader == null && detail && detail.can_edit === true;
              };

              // 🟨 TABLE LAYOUT ONLY, AND THE CONDITION IS DELIBERATE. Product
              // owner: *"yes table layout only for now"*. `expandedContent` is
              // built once per row and handed to whichever row component is
              // active, so WITHOUT the `isTableLayout` term the form would
              // appear in any non-table row. Pinned by
              // `DepartmentOrders.expandEdit.test.js` §2.
              const showInlineEditor = isTableLayout && !!detail && detail.can_edit === true;

              // ── THE SHARED EXPANDED-ROW BODY (extracted 2026-08-16,
              // `components/OrderExpandedRow.js`) ─────────────────────────
              // Builds the PO editor / job-rig / notes / rejection-reason /
              // line-items table (read-only branch) OR the `OrderInlineEditor`
              // form (edit branch) + `OrderExpandedDetails` in BOTH — see the
              // component's own docblock for the full picture. It RESOLVES
              // NOTHING: `canManage` and `showInlineEditor` are resolved HERE,
              // exactly as they always were, and `quickActionsRow` /
              // `quickActionsHeaderGroup` / `adminNotesNode` stay page-built
              // (page-state-coupled, not permission-coupled). Moving JSX grants
              // nobody anything — every gate below is verbatim what it was.
              //
                // 🔻 The per-ORDER `rowCostCodes` / `rowPaymentTypes` / `rowLoadFailures`
              // went with the All Productions mode on 2026-08-31. One department is
              // in view, so the page-level state below is this order's answer.
              // 🚨 `loadFailures` MUST keep travelling with the arrays it describes:
              // without it an emptied `costCodes` reaches `OrderForm` as "this
              // department genuinely has no cost codes" and renders a live select
              // holding only `-- None --`, so a coded line displays as UNCODED.
              const expandedContent = (
                <OrderExpandedRow
                  order={order}
                  detail={detail}
                  showInlineEditor={showInlineEditor}
                  canManage={!!detail && canManageOrder(detail)}
                  testIdPrefix="dept"
                  itemExtraActions={hireActions ? (item) => hireActions.lineItems(order, item, { dirty: editDirtyId === order.id }) : null}
                  itemHeaderTick={hireActions ? (item) => hireActions.lineTick(order, item, { dirty: editDirtyId === order.id }) : null}
                  formatMoney={formatMoney}
                  costCodes={costCodes}
                  paymentTypes={paymentTypes}
                  onCostCodeChange={handleCostCodeChange}
                  onPaymentTypeChange={handlePaymentTypeChange}
                  onItemNotifyUpdated={handleItemNotifyUpdated}
                  onPoNumberSaved={handlePoNumberSaved}
                  onExpectedDeliverySaved={handleExpectedDeliverySaved}
                  showReceivedTicks={showReceivedTicks}
                  canReceiveNow={canReceiveNow}
                  receivingThisOrder={receivingThisOrder}
                  receivedCount={receivedCount}
                  receivableCount={receivableCount}
                  allItemsReceived={allItemsReceived}
                  onToggleItemReceived={receivedHandlers.toggleItem}
                  onReceiveAll={receivedHandlers.receiveAll}
                  canOrderItems={canOrderItems}
                  orderedCount={orderedCount}
                  onToggleItemOrdered={receivedHandlers.toggleOrdered}
                  onOrderAll={receivedHandlers.orderAll}
                  adminNotesNode={adminNotesNode}
                  quickActionsRow={quickActionsRow}
                  quickActionsHeaderGroup={quickActionsHeaderGroup}
                  canUploadDelivery={canUploadDelivery}
                  canRemoveFile={canRemoveOrderFile}
                  onFilesChanged={handleAttachmentsChanged}
                  editor={{
                    productions,
                    suppliers,
                    supplierSuggestions,
                    // Travels WITH the arrays it describes, so a page cannot
                    // hand the form a list without saying whether it landed.
                  loadFailures,
                    gags,
                    subDepartments,
                    userAssignedProductions,
                    collapseRequestSignal,
                    onCollapsePromptResolved: onPromptResolved,
                    onDirtyChange: (isDirty) => handleEditorDirtyChange(order.id, isDirty),
                    onSaved: () => handleEditorSaved(order.id),
                    onCancel: () => { setEditDirtyId(null); setExpandedOrders(new Set()); },
                    saveRequestRef: editorSaveRef,
                    // 2026-10-01 — removing the form's ONLY line hands over to
                    // THIS page's cancel: the status override's `cancelled`
                    // option, i.e. `runRowAction(…, 'cancel')` behind
                    // `canManageOrder` (same gate as `statusJumpNode` above),
                    // plus the one status term that route refuses.
                    onRequestCancelOrder: detail && canManageOrder(detail) && managerMayCancelOrder(detail)
                      ? () => runRowAction(order.id, 'cancel', order)
                      : null,
                  }}
                />
              );
              return expandedContent;
  };

  // The page-level dialogs the row actions open. The CALLER renders this node
  // once, at page level — never inside the Actions menu (see the note above
  // this file's `SkipApprovalsDialog` import for why).
  const dialogs = (
    <>

      {/* ══════════════════════════════════════════════════════════════════
          THE UNSAVED-CHANGES PROMPT (2026-08-10)
          ══════════════════════════════════════════════════════════════════
          Product owner: *"if there are unsaved changes and the user clicks
          approve lets have a popup saying 'un saved changes!' then offer
          buttons like 'save and approve' 'cancel' cancel doesnt undo the
          changes it just stops the approval"*.

          🚨 **A REFINEMENT OF HIS 2026-08-09 "approve butotn should approve
          and save the current state", NOT A REVERSAL.** The action still
          applies to the SAVED order and a failed save still cannot act — the
          save is simply announced now. Do not restore the silent save.

          🔑 **`Keep editing` KEEPS THE EDITS AND DOES NOT ACTION.** It clears
          the pending decision and nothing else: it does not discard, does not
          collapse the row, does not revert the form and does not touch
          `editDirtyId`. That is his explicit instruction — *"cancel doesnt
          undo the changes it just stops the approval"* — and it is the reading
          a developer is most likely to get wrong, because "cancel" next to
          unsaved changes reads as "cancel my edits". Pinned by
          `DepartmentOrders.unsavedActionPrompt.test.js` §3.

          📝 **WHY THE CANCEL SLOT IS NOT LABELLED `Cancel`, given that
          `pages/CLAUDE.md` says "'Cancel' appears NOWHERE" for a REASON that
          does not apply here.** That rule's stated reason is adjacency to
          "Discard changes", and there is no Discard button on this dialog. The
          exception rests on a DIFFERENT argument: this page's status lifecycle
          contains a `cancel` ACTION, so the dialog would otherwise read
          `Cancel` beside `Save and cancel order` — one word for two things, on
          the most destructive item in the menu. Full reasoning at
          `constants/saveThenActPrompt.js`.

          🔴 **NO THIRD BUTTON.** `ConfirmDialog` supports one and "action
          without saving" is the obvious candidate; it is deliberately not
          offered, because actioning a state different from the one on screen
          is exactly what the 2026-08-09 ruling exists to prevent. */}
      <ConfirmDialog
        open={pendingSaveAction !== null}
        title={SAVE_THEN_ACT_TITLE}
        message={pendingSaveAction ? saveThenActCopy(pendingSaveAction.kind).message : null}
        cancelLabel={SAVE_THEN_ACT_KEEP_EDITING}
        confirmLabel={pendingSaveAction ? saveThenActCopy(pendingSaveAction.kind).confirmLabel : ''}
        initialFocus="cancel"
        onCancel={() => setPendingSaveAction(null)}
        onConfirm={() => {
          // Read BEFORE clearing — `setPendingSaveAction(null)` is what unmounts
          // the dialog, and the pending record is the only handle on the action.
          const pending = pendingSaveAction;
          setPendingSaveAction(null);
          if (pending) performSaveThenRun(pending);
        }}
      />

      {/* ══════════════════════════════════════════════════════════════════
          ORDER COMPLETION — THE ALWAYS-SHOWN CONFIRM DIALOG (2026-08-16)
          ══════════════════════════════════════════════════════════════════
          Owner's resolved open-question #2: there is never a no-dialog
          one-click path. The message is computed from the order's state —
          combinable warnings, or a positive summary when all is well — by
          `constants/orderCompletionPrompt.js`, shared with `MyOrders.js` so
          the two surfaces cannot drift on wording. Continue fires
          `runRowAction`, which still goes through save-then-act like every
          other lifecycle item; Cancel clears the prompt and writes nothing. */}
      <ConfirmDialog
        open={completionPrompt !== null}
        title={completionPrompt ? (
          completionPrompt.mode === 'complete'
            ? buildCompleteConfirmMessage({
                order: completionPrompt.order,
                items: completionPrompt.items,
                hasDeliveryPaperwork: completionPrompt.hasDeliveryPaperwork,
              }).title
            : buildReopenConfirmMessage({ order: completionPrompt.order }).title
        ) : ''}
        message={completionPrompt ? (
          completionPrompt.mode === 'complete'
            ? buildCompleteConfirmMessage({
                order: completionPrompt.order,
                items: completionPrompt.items,
                hasDeliveryPaperwork: completionPrompt.hasDeliveryPaperwork,
              }).message
            : buildReopenConfirmMessage({ order: completionPrompt.order }).message
        ) : ''}
        confirmLabel={completionPrompt && completionPrompt.mode === 'complete' ? 'Mark Complete' : 'Re-open'}
        onCancel={() => setCompletionPrompt(null)}
        onConfirm={() => {
          const pending = completionPrompt;
          setCompletionPrompt(null);
          if (pending) runRowAction(pending.order.id, pending.mode === 'complete' ? 'complete' : 'reopen', pending.order);
        }}
      />

      {/* ══════════════════════════════════════════════════════════════════
          APPROVE ON BEHALF (2026-08-18 owner ruling) — see
          `components/ApproveOnBehalfDialog.js`'s docblock for the incident.
          `requestApprove` is the ONLY place `approveOnBehalfPrompt` is set;
          every Approve trigger on this page (the inline button, the
          Actions-menu item, and the Override quick-action) calls it rather
          than `runRowAction`/`handleAction` directly. Cancel writes nothing;
          Confirm reads the pending record BEFORE clearing it (clearing is
          what unmounts the dialog) and replays the exact call the trigger
          would have made — `handleAction` directly for the Override
          quick-action (`direct: true`, matching what it did before this
          change), `runRowAction` — still through save-then-act like every
          other lifecycle item — for everything else. No collapse count is
          passed: `GET /api/orders/department` computes no
          `collapsible_step_count` (only `/pending` and the order-detail
          endpoint do), so this page's copy always reads as one step. */}
      {/* ══════════════════════════════════════════════════════════════════
          SKIP REMAINING APPROVALS (2026-09-13 owner ruling) — ONE DIALOG,
          TWO DOORS
          ══════════════════════════════════════════════════════════════════
          Reached from the `Skip remaining approvals…` menu item AND from
          `InlineStatusJump`'s forward jump past a pending step. `onResolve`
          is what tells them apart; the wording, the mandatory reason and the
          disabled-until-typed Confirm are identical, which is the point.
          Cancel resolves `null` and writes nothing.
          🪤 Read the pending record BEFORE clearing it — clearing is what
          unmounts the dialog. Same shape as the three prompts above. */}
      {/* 🔀 2026-10-03 — the reject / cancel reason, in the page instead of a
          native `window.prompt`. ONE dialog for both kinds and for both doors
          (a row action, and `InlineStatusJump`'s reject), built in
          `useExpandedOrderCore` so this page and My Orders cannot drift on the
          wording or on the required-vs-optional ruling. */}
      {reasonDialog}

      <SkipApprovalsDialog
        open={overridePrompt !== null}
        orderNumber={overridePrompt ? getOrderNumber(overridePrompt.order) : ''}
        approvalInfo={overridePrompt?.order?.approval_info}
        targetStatus={overridePrompt?.targetStatus}
        reason={overrideReason}
        onReasonChange={setOverrideReason}
        onCancel={() => {
          const pending = overridePrompt;
          setOverridePrompt(null);
          setOverrideReason('');
          pending?.onResolve?.(null);
        }}
        onConfirm={() => {
          const pending = overridePrompt;
          // 🪤 TRIMMED HERE, ONCE. The dialog's own `confirmDisabled` already
          // refuses a whitespace-only reason, so this can only ever be a real
          // string — but trimming at the single hand-off point is what keeps
          // both continuations from having to remember.
          const reason = overrideReason.trim();
          setOverridePrompt(null);
          setOverrideReason('');
          pending?.onResolve?.(reason || null);
        }}
      />

      <ApproveOnBehalfDialog
        open={approveOnBehalfPrompt !== null}
        approverName={approveOnBehalfPrompt?.order?.approval_info?.approver_name}
        reason={approveOnBehalfReason}
        onReasonChange={setApproveOnBehalfReason}
        onCancel={() => { setApproveOnBehalfPrompt(null); setApproveOnBehalfReason(''); }}
        onConfirm={(reason) => {
          // 🔀 2026-09-14: `reason` arrives TRIMMED and non-empty — the dialog's
          // Confirm is disabled otherwise — and rides into the POST body.
          const pending = approveOnBehalfPrompt;
          setApproveOnBehalfPrompt(null);
          setApproveOnBehalfReason('');
          if (!pending) return;
          if (pending.direct) {
            handleAction(pending.order.id, 'approve', pending.order, { reason });
          } else {
            runRowAction(pending.order.id, 'approve', pending.order, { reason });
          }
        }}
      />
    </>
  );

  return {
    expandedOrders,
    // Close every opened row (owner 2026-10-03: any tab tap). Callers run it
    // through `continueWhenClean`, so a dirty edit asks first.
    collapseAllOrders: () => setExpandedOrders(new Set()),
    orderDetails,
    detailsLoading,
    editDirtyId,
    currentUser,
    currentUserId,
    currentRole,
    isStoresOnly,
    canActionOrder,
    continueWhenClean,
    toggleExpand,
    handlePoke,
    pokeLoading,
    handleNextStepClick,
    stepInfoFor,
    renderExpanded,
    dialogs,
    // 2026-09-28 — for a caller that mutates an order OUTSIDE this hook (the
    // hires pages' Off hire all / line actions): refresh its DETAIL cache too,
    // the rule every mutation path on an expanded row follows.
    refreshOrderDetail: (orderId) => refreshOneOrder(orderId, setOrdersFromCaller, setOrderDetails, { clearCanAction: false }),
  };
}
